import GameAudioSessionGate from "./GameAudioSessionGate.jsx";
import { listenerContributionFailureMessage, submitReviewedListenerContribution } from "./listenerContribution.js";
/* global globalThis */
import WithdrawalRecovery from "./WithdrawalRecovery.jsx";
import { gameStorage, configureGameIdentityConsent, resolveGameIdentity, forgetGameIdentity } from "./gameIdentity";
// Testing Ignore Build Step
import { v4 as uuidv4 } from 'uuid';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Client, handle_file } from "@gradio/client";
import { useReactMediaRecorder } from "./useResearchRecorder";
import PrivateAudioPanel from "./PrivateAudioPanel";
import { awardAccountXp, mergeDialectXp, persistAccountXp, recoverAccountXp } from "./xpPersistence";
import {
    COLLECTION_CONSENT_SURFACE,
    COLLECTION_CONSENT_VERSION,
    COLLECTION_DATA_RECIPIENT_CATEGORIES,
    COLLECTION_DATASET_LICENSE,
    COLLECTION_DEFAULT_CONSENT_TEXT_SHA256,
    COLLECTION_DEFAULT_CONTROLLER_CONTACT,
    COLLECTION_DEFAULT_CONTROLLER_NAME,
    COLLECTION_PRIVACY_NOTICE_VERSION,
    COLLECTION_RESEARCH_PURPOSES,
    COLLECTION_RETENTION_POLICY_VERSION,
    COLLECTION_WITHDRAWAL_POLICY_VERSION,
    buildCollectionConsentManifest,
    buildCollectionConsentText,
    canEnterCollectionExperience,
    clearGameStoragePreservingCollection,
    clearUserOperationalStorage,
    createCollectionId,
    disableCollectionContributions,
    getCollectionParticipantId,
    getCollectionConsentId,
    getCollectionConsentProfile,
    getCollectionWithdrawalReceipt,
    hasVerifiedCollectionConsent,
    purgeLegacyEmbeddedWalletStorage,
    purgeLegacyTelemetryStorage,
    readExpiringConversationState,
    requestParticipantDeletion,
    sha256CollectionConsentText,
    submitConsentEvent,
    submitJudgmentEvent,
    submitCollectionSubmission,
    writeExpiringConversationState,
} from "./collectionV2";
import { getConsentEntryOutcome, preparePostConsentIdentitySelection } from "./consentFlow";
import { clarifyText, replyText, translateText, speakSelectedLanguage, resolveLanguage, asrHint, validMeaning, mergeLanguageCatalog } from "./languageOperations";
import './App.css';
import './gameShell.css';
import ResearchContributionChoice from "./ResearchContributionChoice.jsx";
const localStorage = gameStorage;
configureGameIdentityConsent(() => ({ allowed: hasVerifiedCollectionConsent(getCollectionParticipantId(null, "pureversation"), "PureVersation") && globalThis.localStorage?.getItem("pureversation_consent_mode") !== "browse-only", participantId: getCollectionParticipantId(null, "pureversation") }));

const nativeTranslate = async (text, targetDialect, sourceDialect, targetLanguage, sourceLanguage) =>
    translateText(await getClient(), text, sourceLanguage, sourceDialect, targetLanguage, targetDialect);


// --- 🎨 GAME ASSETS ---
const GAME_ASSETS = {
    LISTENER: {
        image: process.env.PUBLIC_URL + encodeURI("/assets/the listener mobile.jpg"),
        video: process.env.PUBLIC_URL + encodeURI("/assets/the listener vid.mp4"),
        color: "#38bdf8",
        instructions: [
            "1. Introduce Yourself: Say your name.",
            "2. Speak Naturally: Answer Echo's questions.",
            "3a. Confirm: Okay with the generated texts? Confirm, or (3b)",
            "3b. Edit: Correct the transcript of what you said.",
            "4. Verify: Click on UPDATE MEANING to reload the meaning.",
            "5. Re-Train: Make corrections to the reloaded meaning.",
            "6. Submit: Send your reviewed transcript to the moderated research queue."
        ]
    },
    ARCHIVIST: {
        image: process.env.PUBLIC_URL + encodeURI("/assets/the archivist mobile.jpg"),
        video: process.env.PUBLIC_URL + encodeURI("/assets/the archivist vid.mp4"),
        color: "#facc15",
        instructions: [
            "1. Get a Topic: The Archivist gives you a theme.",
            "2. Record: Tell a short story or monologue.",
            "3. Submit: Send your reviewed story to the moderated research queue."
        ]
    },
    SPEED: {
        image: process.env.PUBLIC_URL + encodeURI("/assets/speed chat mobile.jpg"),
        video: process.env.PUBLIC_URL + encodeURI("/assets/speed chat vid.mp4"),
        color: "#f472b6",
        instructions: [
            "1. Watch the Timer: You have 10 seconds.",
            "2. React Fast: Speak the first thing on your mind.",
            "3. Don't Overthink: Keep the flow going."
        ]
    },
    VISION: {
        image: process.env.PUBLIC_URL + encodeURI("/assets/vision quest mobile.jpg"),
        video: process.env.PUBLIC_URL + encodeURI("/assets/vision quest vid.mp4"),
        color: "#a78bfa",
        instructions: [
            "1. Observe: Look at the image provided.",
            "2. Describe: Explain what you see in detail.",
            "3. Submit: Send your reviewed description to the moderated research queue."
        ]
    },
    BRIDGE: {
        image: process.env.PUBLIC_URL + encodeURI("/assets/convo bridge mobile.png"), // You can update this image later
        video: process.env.PUBLIC_URL + encodeURI("/assets/convo bridge vid.mp4"),
        color: "#22c55e",
        instructions: [
            "1. Setup: Choose a dialect for Player 1 and Player 2.",
            "2. Take Turns: Hold your respective button to speak.",
            "3. Listen: The app will translate and speak to the other player."
        ]
    },
    CONVO: {
        image: process.env.PUBLIC_URL + encodeURI("/assets/human convo mobile.png"),
        video: process.env.PUBLIC_URL + encodeURI("/assets/human convo vid.mp4"),
        color: "#f97316",
        instructions: [
            "1. Connect: Search for an anonymous partner online.",
            "2. Record & Edit: Speak naturally in your dialect.",
            "3. Contribute: With a verified consent receipt, reviewed chat text may enter the moderated research queue.",
            "4. Smart Prompts: AI intervenes to translate heavy slang for your partner."
        ]
    },
    ORACLE: {
        image: process.env.PUBLIC_URL + encodeURI("/assets/vision quest mobile.jpg"), 
        video: process.env.PUBLIC_URL + encodeURI("/assets/vision quest vid.mp4"), 
        color: "#fbbf24",
        instructions: [
            "1. Receive Prompt: You are given an untranslatable idiom or concept.",
            "2. Channel Wisdom: Explain the deep cultural context and pragmatics.",
            "3. Submit: Send your reviewed linguistic explanation for moderation."
        ]
    },
    BURNWARD: {
        image: process.env.PUBLIC_URL + encodeURI("/assets/the archivist mobile.jpg"),
        video: process.env.PUBLIC_URL + encodeURI("/assets/the archivist vid.mp4"),
        color: "#ef4444",
        instructions: [
            "1. Review: Listen to and review fully rejected dataset entries.",
            "2. Decide: If the rejection was unjust, you may file an appeal.",
            "3. Review: Your appeal remains in a minimized controller-held audit queue and is subject to withdrawal handling."
        ]
    }
};

// --- CONFIGURATION ---
const SPACE_URL = "toecm/PureVersation";

const APP_IDENTITY = {
    name: "PureVersation",
    short: "PUREV",
    subtitle: "intra-English dialect mediation"
};

const APP_VERSION = "0.1.0";
const COLLECTION_CONTROLLER_NAME = process.env.REACT_APP_DATA_CONTROLLER_NAME || COLLECTION_DEFAULT_CONTROLLER_NAME;
const COLLECTION_CONTROLLER_CONTACT = process.env.REACT_APP_DATA_CONTROLLER_CONTACT || COLLECTION_DEFAULT_CONTROLLER_CONTACT;
const COLLECTION_CONSENT_MANIFEST = buildCollectionConsentManifest({
    dataControllerName: COLLECTION_CONTROLLER_NAME,
    dataControllerContact: COLLECTION_CONTROLLER_CONTACT,
});
const COLLECTION_CONSENT_TEXT = buildCollectionConsentText({
    dataControllerName: COLLECTION_CONTROLLER_NAME,
    dataControllerContact: COLLECTION_CONTROLLER_CONTACT,
});
const notifyContributionNotRecorded = (error) => window.alert(
    `Research contribution was not recorded or released. ${error?.message || "A verified consent receipt and collection connection are required."}`
);
const toCollectionGameId = (value = "unknown") => String(value)
    .replace(/^Game:\s*/i, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "unknown";

const shouldUseGradioProxy = () => {
    if (typeof window === "undefined") return false;
    return !["localhost", "127.0.0.1"].includes(window.location.hostname);
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Could not read upload for proxy"));
    reader.readAsDataURL(blob);
});

const wrapAudioBlobForUpload = (blob) => {
    if (!blob) return null;
    return shouldUseGradioProxy() ? blob : handle_file(blob);
};

const alterVoiceForCloudUpload = async (blob) => {
    const AudioContextCtor = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
    if (!blob || !AudioContextCtor) {
        throw new Error("Voice privacy transform unavailable; raw audio was not uploaded.");
    }
    let audioContext = null;
    try {
        audioContext = new AudioContextCtor();
        const audioBuffer = await audioContext.decodeAudioData(await blob.arrayBuffer());
        const pitchFactor = 0.92;
        const outLength = Math.max(1, Math.floor(audioBuffer.length / pitchFactor));
        const outBuffer = audioContext.createBuffer(1, outLength, audioBuffer.sampleRate);
        const out = outBuffer.getChannelData(0);
        const channelCount = Math.max(1, audioBuffer.numberOfChannels);
        const sourceChannels = Array.from({ length: channelCount }, (_, index) => audioBuffer.getChannelData(index));

        for (let i = 0; i < outLength; i++) {
            const sourceIndex = i * pitchFactor;
            const left = Math.floor(sourceIndex);
            const right = Math.min(left + 1, audioBuffer.length - 1);
            const mix = sourceIndex - left;
            let sample = 0;
            for (const channel of sourceChannels) {
                sample += channel[left] * (1 - mix) + channel[right] * mix;
            }
            out[i] = Math.max(-1, Math.min(1, sample / channelCount));
        }
        return bufferToWav(outBuffer, outBuffer.length);
    } catch (error) {
        console.warn("Voice privacy transform failed:", error);
        throw new Error("Voice privacy transform failed; raw audio was not uploaded.");
    } finally {
        if (audioContext && typeof audioContext.close === "function") {
            audioContext.close().catch(() => {});
        }
    }
};

const serializeProxyArg = async (arg) => {
    if (typeof Blob !== "undefined" && arg instanceof Blob) {
        return blobToDataUrl(arg);
    }
    if (Array.isArray(arg)) {
        return Promise.all(arg.map(serializeProxyArg));
    }
    return arg;
};

const proxyClient = {
    predict: async (endpoint, args = []) => {
        const processedArgs = await Promise.all((Array.isArray(args) ? args : [args]).map(serializeProxyArg));
        const response = await fetch("/api/gradio-proxy", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ endpoint, args: processedArgs })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(result?.error || `Gradio proxy returned ${response.status}`);
        }
        return result;
    }
};

function AppIdentityBadge() {
    const host = typeof window !== "undefined" ? window.location.hostname : "local";
    return (
        <div className="app-identity-strip" aria-label={`${APP_IDENTITY.name} application identity`}>
            <div className="app-identity-mark">{APP_IDENTITY.short}</div>
            <div className="app-identity-copy">
                <div className="app-identity-title-row">
                    <div className="app-identity-name">{APP_IDENTITY.name}</div>
                    <div className="app-identity-host">{host}</div>
                </div>
                <div className="app-identity-subtitle">{APP_IDENTITY.subtitle}</div>
            </div>
        </div>
    );
}

// 🟢 GLOBAL SINGLETON: Keeps one stable connection for all games
let sharedClient = null;
const getClient = async () => {
    if (shouldUseGradioProxy()) {
        return proxyClient;
    }
    if (!sharedClient) {
        sharedClient = await Client.connect(SPACE_URL);
    }
    return sharedClient;
};

const FALLBACK_DIALECTS = [
    "African American Vernacular English", "American English", "British English", "Gullah Creole", "Indian English", "Indonesian English", "Jamaican Patois", "Korean English", "Malaysian English", "Nigerian English", "Nigerian Pidgin English", "South African English",
    "+ Add New Dialect"
];

const TONES = ["Neutral / Conversational", "Casual / Slang", "Formal / Professional", "Proverb / Idiom"];
const TOPIC_SUGGESTIONS = ["Traffic", "Food", "Weather", "Family", "Work", "Politics", "Football", "Music", "Money", "Dreams"];

const STARTER_QUESTIONS = [
    "What is the best meal you've ever had?",
    "Cats or Dogs? Explain why.",
    "If you could fly, where would you go first?",
    "What is your favorite time of day?",
    "Tell me about a movie you watched recently.",
    "What is your favorite season and why?",
    "If you had a free hour right now, what would you do?"
];

const NEW_GREETINGS = ["Hello!", "Hi there!", "Greetings!", "What's up?", "How far?", "Welcome!"];
const RETURNING_GREETINGS = ["Welcome back!", "Good to see you again!", "Ready for another round?"];
const PRIVACY_STATEMENTS = [
    "Only contributions you review and submit with verified consent enter research moderation.",
    "Reviewed transcripts—not raw audio clips—may enter the moderated research collection.",
    "Browse-only play does not create a research contribution."
];

const LOCAL_PROMPT_ART = [
    GAME_ASSETS.ARCHIVIST.image,
    GAME_ASSETS.SPEED.image,
    GAME_ASSETS.VISION.image,
    GAME_ASSETS.LISTENER.image,
];
const chooseBundledPromptArt = (key, offset = 0) => {
    const hash = Array.from(String(key || "prompt")).reduce((sum, char) => sum + char.charCodeAt(0), offset);
    return LOCAL_PROMPT_ART[Math.abs(hash) % LOCAL_PROMPT_ART.length];
};
const getDoodleUrl = (key) => chooseBundledPromptArt(key);
const getPhotoUrl = (key) => chooseBundledPromptArt(key, 1);
const getRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

const CONSENT_MODALITY_OPTIONS = [
    { value: "text", label: "I permit my reviewed text/transcripts and annotations to enter the moderated research queue." },
    { value: "judgment", label: "I permit my peer-review decisions and corrections to be collected." },
    { value: "speech", label: "I permit speech processing to create a reviewed transcript. This does not authorize storing recordings; optional private recording consent is separate in Data & consent." },
    { value: "multimodal", label: "I permit reviewed multimodal task annotations derived from selected game inputs (not private image uploads)." },
];
const CONSENT_ACKNOWLEDGMENT_OPTIONS = [
    { key: "public_release_confirmed", label: "I understand accepted contributions may be publicly released under CC BY 4.0 and downloaded copies may not be retractable. (Required)" },
    { key: "commercial_ai_reuse_confirmed", label: "I understand CC BY 4.0 permits commercial reuse, including AI research and development. (Required)" },
    { key: "privacy_risks_acknowledged", label: "I acknowledge that text, speech, dialect, and context may reveal identity or sensitive traits, and I will not submit personal or sensitive information. (Required)" },
    { key: "operational_processing_acknowledged", label: "I understand selected AI features may transmit inputs to configured AI and hosting providers under their terms, including international processing; browse-only prevents research collection, not this requested operational processing. (Required)" },
    { key: "voluntary_participation_confirmed", label: "I confirm participation and each contribution are voluntary, and I may instead play in browse-only mode without penalty. (Required)" },
    { key: "contribution_rights_confirmed", label: "I confirm I have the rights and authorization needed to contribute this material. (Required)" },
    { key: "eligibility_confirmed", label: "I confirm I am at least 18, or the age of legal consent where I live. (Required)" },
];

export function ConsentScreen({ onConsent, onBrowseOnly, onWithdraw }) {
    const [acknowledgments, setAcknowledgments] = useState(() => Object.fromEntries(
        CONSENT_ACKNOWLEDGMENT_OPTIONS.map(({ key }) => [key, false])
    ));
    const [modalities, setModalities] = useState([]);
    const selectAllRef = useRef(null);
    const canConsent = Object.values(acknowledgments).every(Boolean) && modalities.length > 0;
    const allShownSelected = Object.values(acknowledgments).every(Boolean)
        && modalities.length === CONSENT_MODALITY_OPTIONS.length;
    const anyShownSelected = Object.values(acknowledgments).some(Boolean) || modalities.length > 0;
    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = anyShownSelected && !allShownSelected;
        }
    }, [allShownSelected, anyShownSelected]);
    const toggleAcknowledgment = (key) => setAcknowledgments(current => ({ ...current, [key]: !current[key] }));
    const toggleModality = (value) => setModalities(current => (
        current.includes(value) ? current.filter(item => item !== value) : [...current, value]
    ));
    const toggleAllShownChoices = (checked) => {
        setModalities(checked ? CONSENT_MODALITY_OPTIONS.map(({ value }) => value) : []);
        setAcknowledgments(Object.fromEntries(
            CONSENT_ACKNOWLEDGMENT_OPTIONS.map(({ key }) => [key, checked])
        ));
    };
    const checkboxStyle = { display: 'flex', alignItems: 'flex-start', gap: '9px', margin: '9px 0', cursor: 'pointer' };

    return (
        <div className="setup-screen consent-screen">
            <div className="consent-screen-heading">
                <div className="icon-large consent-screen-icon">⚖️</div>
                <h2>ETHICAL AI CONSENT</h2>
            </div>
            <div
                className="consent-scroll-region"
                role="region"
                aria-label="Ethical AI consent information and choices"
                tabIndex="0"
            >
            <WithdrawalRecovery />
            <div className="consent-notice">
                <p style={{ marginTop: 0, color: '#f8fafc', fontWeight: 'bold', fontSize: '14px' }}>
                    Choose whether—and how—you contribute research data.
                </p>
                <p><strong>Controller:</strong> {COLLECTION_CONTROLLER_NAME} · <a href={COLLECTION_CONTROLLER_CONTACT} target="_blank" rel="noreferrer" style={{ color: '#38bdf8' }}>{COLLECTION_CONTROLLER_CONTACT}</a></p>
                <p><strong>Recipients:</strong> project research team, approved human reviewers, configured AI and hosting service providers, public dataset users, and researchers and AI developers.</p>
                <ul style={{ paddingLeft: '18px', display: 'grid', gap: '6px' }}>
                    <li>Only contributions covered by a verified receipt and approved human moderation are eligible for research release.</li>
                    <li>Text and judgment collection remain separate from optional private recording storage. Raw recordings are never publicly released through these apps.</li>
                    <li>Game XP and ranks are local gameplay features, not payment, tokens, authorship, or a promise that a contribution will be accepted.</li>
                    <li>Accepted contributions may be released under CC BY 4.0. Rejected content is not published; controller-held submission and audit records remain subject to the retention and withdrawal policy described below.</li>
                    <li>You may request withdrawal or deletion of controller-held data; already distributed downstream copies may not be fully retractable.</li>
                    <li>Your private withdrawal credential stays only in this browser and never enters research datasets. Clearing this site's data can remove your ability to authenticate withdrawal; ordinary in-game reset preserves the credential.</li>
                    <li>The app may temporarily store game progress, consent credentials, language preferences, and conversation state in this browser. Conversation state expires after 24 hours and is cleared on successful withdrawal or game-data reset. Do not use a shared device for sensitive material.</li>
                    <li>Live matchmaking messages remain only in ephemeral server memory for up to one hour for delivery; they are not research contributions unless you separately submit reviewed text.</li>
                    <li>Each optional contribution normally takes only the time needed to review and submit one game turn. There is no direct benefit or compensation; the hoped-for benefit is improved inclusive language research and tools. You may instead play without contributing.</li>
                    <li>Use the controller contact above for questions or complaints about the research, privacy, withdrawal, or participant rights.</li>
                </ul>
                <p><strong>Policy bundle:</strong> {COLLECTION_CONSENT_VERSION} · {COLLECTION_PRIVACY_NOTICE_VERSION} · {COLLECTION_RETENTION_POLICY_VERSION} · {COLLECTION_WITHDRAWAL_POLICY_VERSION}</p>

                <details style={{ margin: '10px 0' }} open>
                    <summary style={{ color: '#f8fafc', cursor: 'pointer', fontWeight: 'bold' }}>Full research notice</summary>
                    <div style={{ marginTop: '8px', display: 'grid', gap: '7px' }}>
                        {Object.entries(COLLECTION_CONSENT_MANIFEST.notice).map(([key, value]) => (
                            <p key={key} style={{ margin: 0 }}><strong>{key.replaceAll('_', ' ')}:</strong> {value}</p>
                        ))}
                    </div>
                </details>

                <div className="consent-select-all">
                    <label style={checkboxStyle}>
                        <input
                            ref={selectAllRef}
                            type="checkbox"
                            data-testid="consent-select-all"
                            checked={allShownSelected}
                            aria-checked={anyShownSelected && !allShownSelected ? "mixed" : allShownSelected}
                            aria-describedby="consent-select-all-help"
                            onChange={event => toggleAllShownChoices(event.target.checked)}
                        />
                        <span><strong>Select all shown contribution types and required confirmations</strong></span>
                    </label>
                    <small id="consent-select-all-help">Optional shortcut only. You can change any individual choice below. Raw audio remains excluded.</small>
                </div>

                <fieldset style={{ border: '1px solid #475569', borderRadius: '8px', margin: '12px 0', padding: '10px' }}>
                    <legend style={{ color: '#22c55e', fontWeight: 'bold' }}>Select each contribution type you authorize</legend>
                    {CONSENT_MODALITY_OPTIONS.map(option => (
                        <label key={option.value} style={checkboxStyle}>
                            <input data-testid={`consent-modality-${option.value}`} type="checkbox" checked={modalities.includes(option.value)} onChange={() => toggleModality(option.value)} />
                            <span>{option.label}</span>
                        </label>
                    ))}
                </fieldset>

                <fieldset style={{ border: '1px solid #475569', borderRadius: '8px', margin: '12px 0 0', padding: '10px' }}>
                    <legend style={{ color: '#facc15', fontWeight: 'bold' }}>Required active confirmations</legend>
                    {CONSENT_ACKNOWLEDGMENT_OPTIONS.map(option => (
                        <label key={option.key} style={checkboxStyle}>
                            <input data-testid={`consent-confirmation-${option.key}`} type="checkbox" checked={acknowledgments[option.key]} onChange={() => toggleAcknowledgment(option.key)} />
                            <span>{option.label}</span>
                        </label>
                    ))}
                </fieldset>
            </div>
            </div>

            <div className="consent-actions">
                <button type="button" className="cyber-button" disabled={!canConsent} onClick={() => onConsent({ permitted_modalities: modalities, acknowledgments })} style={{ background: canConsent ? '#22c55e' : '#475569', opacity: canConsent ? 1 : 0.65 }}>
                    I UNDERSTAND & CONSENT
                </button>
                <button type="button" className="cancel-btn" onClick={onBrowseOnly}>
                    PLAY WITHOUT RESEARCH CONTRIBUTION
                </button>
                <button type="button" className="cancel-btn consent-withdraw-button" onClick={onWithdraw}>
                    DATA & CONSENT: REQUEST WITHDRAWAL / DELETION
                </button>
            </div>
        </div>
    );
}

export function DataRightsPanel({ receiptStatus, onWithdraw, onStartNewConsent }) {
    const participantId = getCollectionParticipantId(
        localStorage.getItem("pureversation_session_id"), "pureversation"
    );
    const ready = hasVerifiedCollectionConsent(participantId, APP_IDENTITY.name);
    const consentProfile = getCollectionConsentProfile(participantId, APP_IDENTITY.name);
    const withdrawalReceipt = getCollectionWithdrawalReceipt(participantId);
    const summaryStatus = withdrawalReceipt
        ? "withdrawal requested"
        : ready
            ? "receipt verified"
            : receiptStatus === "BROWSING ONLY"
                ? "BROWSING ONLY"
                : "contributions paused";

    return (
        <details className="data-rights-panel">
            <summary className={ready ? "data-rights-ready" : "data-rights-paused"}>
                Data &amp; consent — {summaryStatus}
            </summary>
            <div className="data-rights-content" aria-live="polite">
                <p><strong>Participant ID:</strong> <code>{participantId}</code></p>
                <p><strong>Consent receipt:</strong> {getCollectionConsentId(participantId, APP_IDENTITY.name) || "Not available — no research contribution will be sent."}</p>
                {consentProfile?.consent_record_sha256 && <p><strong>Server receipt SHA-256:</strong> <code>{consentProfile.consent_record_sha256}</code></p>}
                {consentProfile?.permitted_modalities?.length > 0 && <p><strong>Authorized contribution types:</strong> {consentProfile.permitted_modalities.join(", ")}</p>}
                <p><strong>Policy:</strong> {COLLECTION_CONSENT_VERSION} · privacy {COLLECTION_PRIVACY_NOTICE_VERSION} · retention {COLLECTION_RETENTION_POLICY_VERSION} · withdrawal {COLLECTION_WITHDRAWAL_POLICY_VERSION} · hash {(consentProfile?.consent_text_sha256 || COLLECTION_DEFAULT_CONSENT_TEXT_SHA256).slice(0, 12)}…</p>
                <p className="data-rights-warning"><strong>Keep your browser data:</strong> Your private withdrawal credential stays only in this browser and never enters a dataset. Clearing this site’s data can remove your ability to authenticate a later withdrawal; an ordinary game reset preserves it.</p>
                {withdrawalReceipt && <p className="data-rights-warning">Request {withdrawalReceipt.deletion_request_id}: controller-held data is {withdrawalReceipt.local_pending_audit_status || "being tombstoned"}; public export propagation is {withdrawalReceipt.remote_export_status || "pending"}. Downstream CC BY 4.0 copies may remain.</p>}
                {ready ? (
                    <button type="button" className="cancel-btn" onClick={onWithdraw}>Withdraw consent / request deletion</button>
                ) : (
                    <button type="button" className="cyber-button" onClick={onStartNewConsent}>Review consent to contribute</button>
                )}
            </div>
            <PrivateAudioPanel />
            <WithdrawalRecovery />
        </details>
    );
}

// ==========================================
// 📺 TUTORIAL MODAL
// ==========================================
function GameTutorial({ gameKey, onStart, onCancel }) {
    const asset = GAME_ASSETS[gameKey];
    const [isMuted, setIsMuted] = useState(true);

    if (!asset) return null;

    return (
        <div className="tutorial-overlay" style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(15, 23, 42, 0.95)', zIndex: 1000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(10px)'
        }}>
            <div className="tutorial-card" style={{
                background: '#1e293b', padding: '20px', borderRadius: '20px',
                border: `1px solid ${asset.color}`, maxWidth: '90%', width: '400px',
                textAlign: 'center', boxShadow: `0 0 30px ${asset.color}40`
            }}>
                <h2 style={{ color: asset.color, margin: '0 0 15px 0' }}>HOW TO PLAY</h2>

                <div
                    style={{ borderRadius: '12px', overflow: 'hidden', marginBottom: '10px', border: '1px solid #334155', background: '#000', position: 'relative', cursor: 'pointer' }}
                    onClick={() => setIsMuted(!isMuted)}
                >
                    <video src={asset.video} autoPlay loop muted={isMuted} playsInline style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                    {isMuted && (
                        <div style={{ position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(0,0,0,0.6)', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', color: '#fff' }}>
                            🔇 Tap to Unmute
                        </div>
                    )}
                </div>

                <div style={{ textAlign: 'left', marginBottom: '25px', color: '#cbd5e1', fontSize: '14px', marginTop: '15px' }}>
                    {asset.instructions.map((step, i) => (
                        <p key={i} style={{ marginBottom: '8px' }}>✅ {step}</p>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={onCancel} className="cancel-btn" style={{ flex: 1, padding: '15px' }}>BACK</button>
                    <button onClick={onStart} className="cyber-button" style={{ flex: 1, background: asset.color, color: '#000', fontWeight: 'bold' }}>START GAME 🚀</button>
                </div>
            </div>
        </div>
    );
}

// ==========================================
// FEEDBACK NOTICE
// ==========================================
function FeedbackModal({ onClose }) {
    return (
        <div className="tutorial-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(15, 23, 42, 0.95)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)' }}>
            <div className="mission-card" style={{ background: '#1e293b', padding: '20px', borderRadius: '15px', border: '1px solid #a78bfa', width: '90%', maxWidth: '400px', display: 'flex', flexDirection: 'column', height: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ color: '#a78bfa', margin: 0 }}>FEEDBACK UNAVAILABLE</h3>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#ef4444', fontSize: '18px', cursor: 'pointer' }}>✖</button>
                </div>
                <div role="status" style={{ background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '8px', borderLeft: '3px solid #38bdf8', marginBottom: '15px', fontSize: '13px', color: '#cbd5e1' }}>
                    Feedback submission is disabled until it has a purpose-specific privacy notice, receipt, and private storage path. This screen does not collect text, operator IDs, screenshots, or interaction analytics.
                </div>
                <button className="cyber-button" onClick={onClose} style={{ background: '#a78bfa', color: '#000' }}>CLOSE</button>
            </div>
        </div>
    );
}

// ==========================================
// 🔐 ANONYMOUS GAME IDENTITY
// ==========================================

// ==========================================
// 🚀 MAIN APP COMPONENT
// ==========================================
function App() {
    const [hasIdentity, setHasIdentity] = useState(() => {
        purgeLegacyEmbeddedWalletStorage();
        purgeLegacyTelemetryStorage();
        return false;
    });
    const [hasConsented, setHasConsented] = useState(() => {
        const participantId = getCollectionParticipantId(
            localStorage.getItem("pureversation_session_id"), "pureversation"
        );
        return canEnterCollectionExperience({
            participantId,
            appId: APP_IDENTITY.name,
            mode: localStorage.getItem("pureversation_consent_mode") || "",
        });
    });

    const [showFeedback, setShowFeedback] = useState(false);

    const [showUpdatePopup, setShowUpdatePopup] = useState(false);
    const [swRegistration, setSwRegistration] = useState(null);

    useEffect(() => {
        const handleSWUpdate = (event) => {
            setSwRegistration(event.detail);
            setShowUpdatePopup(true);
        };
        const handleControllerChange = () => {
            window.location.reload();
        };

        window.addEventListener('swUpdated', handleSWUpdate);
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
        }

        return () => {
            window.removeEventListener('swUpdated', handleSWUpdate);
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
            }
        };
    }, []);

    const applyUpdate = () => {
        if (swRegistration && swRegistration.waiting) {
            swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
            setShowUpdatePopup(false);
            // The reload will happen safely in the controllerchange listener
        }
    };

    const [activeGame, setActiveGame] = useState(() => localStorage.getItem("pure_active_game") || "HOME");
    
    useEffect(() => {
        localStorage.setItem("pure_active_game", activeGame);
    }, [activeGame]);
    const [cloudStatus, setCloudStatus] = useState("⚪ CHECKING SYNC...");
    const [userKey, setUserKey] = useState(null);
    const [address, setAddress] = useState("");
    const [xp, setXP] = useState(recoverAccountXp);
    const [collectionConsentStatus, setCollectionConsentStatus] = useState(() => {
        const participantId = getCollectionParticipantId(localStorage.getItem("pureversation_session_id"), "pureversation");
        if (hasVerifiedCollectionConsent(participantId, APP_IDENTITY.name)) return "RECORDED";
        return localStorage.getItem("pureversation_consent_mode") === "browse-only" ? "BROWSING ONLY" : "NOT RECORDED";
    });
    const [isWithdrawalPending, setIsWithdrawalPending] = useState(false);
    const [consentTransitionNotice, setConsentTransitionNotice] = useState("");
    const consentOperationRef = useRef(0);

    const unlockGame = (mode) => {
        localStorage.setItem("pureversation_consent_mode", mode);
        const identity = resolveGameIdentity();
        setUserKey(identity.sessionID);
        setAddress(identity.opAddr);
        setXP(recoverAccountXp());

        preparePostConsentIdentitySelection();
        setActiveGame("HOME");
        setHasIdentity(true);
        setHasConsented(true);
        localStorage.setItem("pureversation_consented", "true");
        localStorage.setItem("pureversation_consent_version", COLLECTION_CONSENT_VERSION);
        localStorage.setItem("pureversation_consent_mode", mode);
    };

    const handleGlobalConsent = async ({ permitted_modalities: permittedModalities, acknowledgments }) => {
        const consentOperation = ++consentOperationRef.current;
        localStorage.removeItem("pureversation_consented");
        localStorage.removeItem("pureversation_consent_mode");
        setHasConsented(false);
        setCollectionConsentStatus("RECORDING...");
        const participantId = getCollectionParticipantId(
            userKey || localStorage.getItem("pureversation_session_id"),
            "pureversation", { create: true }
        );
        try {
            const consentTextSha256 = await sha256CollectionConsentText(COLLECTION_CONSENT_TEXT);
            if (consentOperation !== consentOperationRef.current) return;
            const result = await submitConsentEvent({
                    participant_id: participantId,
                    app_id: APP_IDENTITY.name,
                    app_version: APP_VERSION,
                    consent_version: COLLECTION_CONSENT_VERSION,
                    privacy_notice_version: COLLECTION_PRIVACY_NOTICE_VERSION,
                    permitted_modalities: permittedModalities,
                    dataset_license: COLLECTION_DATASET_LICENSE,
                    consent_granted: true,
                    consent_surface: COLLECTION_CONSENT_SURFACE,
                    consent_locale: "en",
                    consent_text_sha256: consentTextSha256,
                    research_purposes: [...COLLECTION_RESEARCH_PURPOSES],
                    retention_policy_version: COLLECTION_RETENTION_POLICY_VERSION,
                    withdrawal_policy_version: COLLECTION_WITHDRAWAL_POLICY_VERSION,
                    ...acknowledgments,
                    raw_audio_consent: false,
                    data_controller_name: COLLECTION_CONTROLLER_NAME,
                    data_controller_contact: COLLECTION_CONTROLLER_CONTACT,
                    data_recipient_categories: [...COLLECTION_DATA_RECIPIENT_CATEGORIES],
            });
            const outcome = getConsentEntryOutcome({
                receiptVerified: result.receipt_verified,
                researchAccessAvailable: canEnterCollectionExperience({
                    participantId, appId: APP_IDENTITY.name, mode: "research-consent",
                }),
            });
            if (consentOperation !== consentOperationRef.current) return;
            if (outcome.shouldUnlock) {
                localStorage.setItem("pureversation_consent_modalities", JSON.stringify(permittedModalities));
                unlockGame(outcome.mode);
            } else {
                localStorage.removeItem("pureversation_consent_modalities");
                setHasConsented(false);
            }
            setCollectionConsentStatus(outcome.status);
            setConsentTransitionNotice(result.reenrollment_required ? result.error : outcome.notice);
        } catch (error) {
            console.warn("Consent receipt could not be recorded:", error);
            if (consentOperation !== consentOperationRef.current) return;
            const outcome = getConsentEntryOutcome();
            localStorage.removeItem("pureversation_consent_modalities");
            setHasConsented(false);
            setCollectionConsentStatus(outcome.status);
            setConsentTransitionNotice(outcome.notice);
        }
    };

    const handleBrowseOnly = () => {
        consentOperationRef.current += 1;
        const participantId = getCollectionParticipantId(userKey || localStorage.getItem("pureversation_session_id"), "pureversation");
        disableCollectionContributions(participantId, APP_IDENTITY.name);
        localStorage.removeItem("pureversation_consent_modalities");
        unlockGame("browse-only");
        setCollectionConsentStatus("BROWSING ONLY");
        setConsentTransitionNotice("");
    };

    const handleOpenConsentManager = () => {
        setHasConsented(false);
    };

    const handleWithdrawal = async () => {
        if (isWithdrawalPending) return;
        const participantId = getCollectionParticipantId(userKey || localStorage.getItem("pureversation_session_id"), "pureversation");
        const confirmed = window.confirm(
            `Request withdrawal/deletion for participant ${participantId}? Controller-held records will be tombstoned, but public or downstream CC-BY-4.0 copies may remain.`
        );
        if (!confirmed) return;
        setIsWithdrawalPending(true);
        try {
            const result = await requestParticipantDeletion(participantId, {
                appId: APP_IDENTITY.name,
                participantNamespace: "pureversation",
                reason: "consent_withdrawal",
            });
            clearUserOperationalStorage(userKey);
            forgetGameIdentity();
            purgeLegacyTelemetryStorage();
            localStorage.removeItem("pureversation_consented");
            localStorage.removeItem("pureversation_consent_version");
            localStorage.removeItem("pureversation_consent_mode");
            localStorage.removeItem("pureversation_consent_modalities");
            setHasConsented(false);
            setCollectionConsentStatus("WITHDRAWAL REQUESTED");
            window.alert(
                `Tracked request ${result.data.deletion_request_id}: ${result.data.status}. `
                + `Controller-held queue: ${result.data.local_pending_audit_status}; public export: ${result.data.remote_export_status}. `
                + "Your withdrawal credential is preserved; a new participant ID is created only if you opt in again."
            );
        } catch (error) {
            window.alert(`Withdrawal request was not recorded. No completion is claimed. ${error.message}`);
        } finally {
            setIsWithdrawalPending(false);
        }
    };

    useEffect(() => {
        persistAccountXp(xp);
    }, [xp]);

    const getRank = (points) => {
        if (points < 100) return "Initiate";
        if (points < 300) return "Scout";
        if (points < 800) return "Linguist";
        if (points < 1500) return "Archivist";
        return "Oracle";
    };

    const [dialects, setDialects] = useState(FALLBACK_DIALECTS);
    const [languageHierarchy, setLanguageHierarchy] = useState({"English": FALLBACK_DIALECTS, "Korean": ["Seoul Dialect", "Jeju Dialect", "+ Add New Dialect"], "Other": ["+ Add New Dialect"], "+ Add New Language": ["+ Add New Dialect"]});
    const [greeting, setGreeting] = useState("");
    const [isPulsing, setIsPulsing] = useState(false);

    useEffect(() => {
        const openFb = () => setShowFeedback(true);
        window.addEventListener('open-feedback', openFb);
        return () => window.removeEventListener('open-feedback', openFb);
    }, []);

    useEffect(() => {
        const identity = resolveGameIdentity();
        setUserKey(identity.sessionID);
        setAddress(identity.opAddr);
        setHasIdentity(true);
        const greetList = identity.persistent ? RETURNING_GREETINGS : NEW_GREETINGS;
        setGreeting(`${getRandom(greetList)} ${getRandom(PRIVACY_STATEMENTS)}`);

        const checkSync = async () => {
            try {
                const app = await getClient();
                const res = await app.predict("/check_cloud_sync");
                setCloudStatus(res.data[0]);
            } catch (e) { setCloudStatus("🔴 SYNC OFFLINE"); }
        };

        const loadDialects = async () => {
            try {
                const app = await getClient();
                const res = await app.predict("/api_get_language_hierarchy");

                console.log("📡 RAW DIALECT DATA RECEIVED:", res.data[0]);

                const rawData = res.data[0];
                let parsedDict = {};

                if (typeof rawData === "string") {
                    parsedDict = JSON.parse(rawData);
                } else if (typeof rawData === "object" && rawData !== null) {
                    parsedDict = rawData;
                }

                // Retain saved/default choices when a delayed or partial catalog arrives.
                mergeLanguageCatalog({}, parsedDict); // Reject malformed and Test Dialect-only catalogs.
                setLanguageHierarchy(previous => mergeLanguageCatalog(previous, parsedDict));
                if (parsedDict.English) setDialects(previous => [...new Set([...previous, ...parsedDict.English])]);

            } catch (e) {
                console.error("⚠️ Failed to sync database dialects, using fallbacks:", e);
                // Keep the current catalog and selections; do not replace them with a test-only response.
            }
        };

        // 🟢 FIX: Run them one after the other so Hugging Face doesn't block the simultaneous connection requests
        const initializeApp = async () => {
            await checkSync();
            await loadDialects();
        };

        initializeApp();

        const syncInterval = setInterval(checkSync, 300000);
        return () => clearInterval(syncInterval);
    }, []);

    const handleIDClick = () => {
        navigator.clipboard.writeText(address);
        setIsPulsing(true);
        setTimeout(() => setIsPulsing(false), 600);
    };

    const handleResetGame = () => {
        clearUserOperationalStorage(userKey);
        purgeLegacyTelemetryStorage();
        clearGameStoragePreservingCollection();
        window.location.reload();
    };

    return (
        <div className={`App${hasConsented && hasIdentity ? " game-shell-active" : ""}`}>
            {showUpdatePopup && (
                <div style={{
                    position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
                    background: '#f97316', color: '#000', padding: '15px 25px', borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', gap: '15px',
                    fontWeight: 'bold', border: '2px solid #fff'
                }}>
                    <span>🚀 A new version is available!</span>
                    <button onClick={applyUpdate} style={{
                        background: '#000', color: '#f97316', border: 'none', padding: '8px 16px', borderRadius: '8px',
                        cursor: 'pointer', fontWeight: 'bold'
                    }}>Refresh</button>
                </div>
            )}
            <div className={`cyber-container ${!hasConsented || !hasIdentity ? "pre-app-container" : ""}`.trim()}>
                <AppIdentityBadge />

                <GameAudioSessionGate gameId={hasConsented ? activeGame : "HOME"} />

                {hasConsented && (
                    <DataRightsPanel
                        receiptStatus={collectionConsentStatus}
                        onWithdraw={handleWithdrawal}
                        onStartNewConsent={handleOpenConsentManager}
                    />
                )}

                {consentTransitionNotice && (
                    <div className="consent-transition-notice" role="status">
                        <span>{consentTransitionNotice}</span>
                        <button type="button" onClick={() => setConsentTransitionNotice("")} aria-label="Dismiss consent status">×</button>
                    </div>
                )}

                {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}

                {!hasConsented ? (
                    <ConsentScreen onConsent={handleGlobalConsent} onBrowseOnly={handleBrowseOnly} onWithdraw={handleWithdrawal} />
                ) : !hasIdentity ? (
                    <div role="status" className="setup-screen">Loading your games…</div>
                ) : (
                    <>
                        <div className="hud-header">
                            <div className="xp-badge" style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                <span>✨ {xp} XP</span>
                                <span style={{ opacity: 0.5 }}>|</span>
                                <span style={{ color: '#a78bfa', fontWeight: '900' }}>{getRank(xp)}</span>
                            </div>
                            <div className="status-badge" style={{ marginLeft: '10px', background: 'rgba(0,0,0,0.5)' }}>☁️ {cloudStatus}</div>
                            <button type="button" className="status-badge" onClick={handleOpenConsentManager} disabled={isWithdrawalPending} style={{ marginLeft: '10px', cursor: 'pointer', color: '#fca5a5' }} title="Review choices or request withdrawal">DATA & CONSENT</button>
                            <div className="status-badge" style={{ marginLeft: 'auto' }} title="Research contribution consent status">CONSENT: {collectionConsentStatus}</div>
                            <div className="status-badge">MODE: {activeGame}</div>
                        </div>

                        {(!userKey && activeGame !== "HOME") ? (
                            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#38bdf8' }}>
                                <h3>INITIALIZING SECURE LINK...</h3>
                            </div>
                        ) : (
                            <>
                                {activeGame === "HOME" && <HomeMenu onSelect={setActiveGame} greeting={greeting} onResetGame={handleResetGame} xp={xp} />}

                        {activeGame === "CONVO" && (
                            <GameHumanConvo
                                userKey={userKey} operator={address} dialects={dialects} languageHierarchy={languageHierarchy}
                                onBack={() => setActiveGame("HOME")}
                                setXP={setXP}
                            />
                        )}

                        {activeGame === "BRIDGE" && (
                            <GameConvoBridge
                                userKey={userKey} dialects={dialects} languageHierarchy={languageHierarchy} operator={address}
                                onBack={() => setActiveGame("HOME")}
                                setXP={setXP}
                            />
                        )}

                        {activeGame === "ARCHIVIST" && (
                            <GameArchivist
                                userKey={userKey} setXP={setXP} dialects={dialects} languageHierarchy={languageHierarchy} setDialects={setDialects} operator={address}
                                onBack={() => setActiveGame("HOME")} greeting={greeting}
                            />
                        )}

                        {activeGame === "SPEED" && (
                            <GameSpeedChat
                                userKey={userKey} setXP={setXP} dialects={dialects} languageHierarchy={languageHierarchy} setDialects={setDialects} operator={address}
                                onBack={() => setActiveGame("HOME")} greeting={greeting}
                            />
                        )}

                        {activeGame === "VISION" && (
                            <GameVisionQuest
                                userKey={userKey} setXP={setXP} dialects={dialects} languageHierarchy={languageHierarchy} setDialects={setDialects} operator={address}
                                onBack={() => setActiveGame("HOME")} greeting={greeting}
                            />
                        )}

                        {activeGame === "LISTENER" && (
                            <GameActiveListener
                                userKey={userKey} setXP={setXP} dialects={dialects} languageHierarchy={languageHierarchy} setDialects={setDialects} operator={address}
                                onBack={() => setActiveGame("HOME")}
                            />
                        )}

                        {activeGame === "ORACLE" && (
                            <GameLinguisticOracle
                                userKey={userKey} setXP={setXP} dialects={dialects} languageHierarchy={languageHierarchy} setDialects={setDialects} operator={address}
                                onBack={() => setActiveGame("HOME")} greeting={greeting}
                            />
                        )}

                        {activeGame === "BURNWARD" && (
                            <GameBurnWard
                                userKey={userKey} dialects={dialects} languageHierarchy={languageHierarchy} operator={address}
                                onBack={() => setActiveGame("HOME")}
                            />
                        )}
                            </>
                        )}

                        <div className="app-footer">
                            <div
                                className={`id-footer ${isPulsing ? 'pulse-active' : ''}`} onClick={handleIDClick} title="Tap to Copy Operator ID"
                                style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px', transition: 'all 0.3s ease', flex: 1 }}
                            >
                                <div style={{ fontWeight: 'bold', color: '#38bdf8' }}>OPERATOR: {address.slice(0, 6)}...{address.slice(-4)}</div>
                                <div style={{ fontSize: '9px', opacity: 0.8, color: '#facc15', letterSpacing: '1px', marginTop: '-2px' }}>(Tap to Copy)</div>
                                <div style={{ fontSize: '10px', opacity: 0.6, letterSpacing: '1px', fontFamily: 'monospace', marginTop: '2px' }}>{userKey || "GENERATING..."}</div>
                            </div>

                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function HomeMenu({ onSelect, greeting, onResetGame, xp }) {
    const [selectedTutorial, setSelectedTutorial] = useState(null);
    const handleGameClick = (gameKey) => setSelectedTutorial(gameKey);
    const confirmStart = () => { onSelect(selectedTutorial); setSelectedTutorial(null); };

    return (
        <div className="home-layout">
            {selectedTutorial && <GameTutorial gameKey={selectedTutorial} onStart={confirmStart} onCancel={() => setSelectedTutorial(null)} />}
            <div className="welcome-banner"><p>{greeting}</p></div>
            <div className="instruction-zone"><p>SELECT MISSION</p><div className="instruction-line"></div></div>
            <div className="menu-grid" style={{ paddingBottom: '40px' }}>

                <div className="menu-card" onClick={() => handleGameClick("LISTENER")} style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${GAME_ASSETS.LISTENER.image})`, backgroundSize: 'cover' }}>
                    <div className="icon-large">👂</div><h3>THE LISTENER</h3><p>Active Conversation Mode</p>
                </div>
                <div className="menu-card" onClick={() => handleGameClick("ARCHIVIST")} style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${GAME_ASSETS.ARCHIVIST.image})`, backgroundSize: 'cover' }}>
                    <div className="icon-large">📜</div><h3>THE ARCHIVIST</h3><p>Daily Journaling</p>
                </div>
                <div className="menu-card" onClick={() => handleGameClick("SPEED")} style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${GAME_ASSETS.SPEED.image})`, backgroundSize: 'cover' }}>
                    <div className="icon-large">⚡</div><h3>SPEED CHAT</h3><p>Rapid Fire Questions</p>
                </div>
                <div className="menu-card" onClick={() => handleGameClick("VISION")} style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${GAME_ASSETS.VISION.image})`, backgroundSize: 'cover' }}>
                    <div className="icon-large">👁️</div><h3>VISION QUEST</h3><p>Describe what you see</p>
                </div>
                <div className="menu-card" onClick={() => handleGameClick("BRIDGE")} style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${GAME_ASSETS.BRIDGE.image})`, backgroundSize: 'cover', border: '1px solid #22c55e' }}>
                    <div className="icon-large">🌉</div><h3 style={{ color: '#22c55e' }}>CONVO BRIDGE</h3><p>Live 2-Player Translation</p>
                </div>
                <div className="menu-card" onClick={() => handleGameClick("CONVO")} style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${GAME_ASSETS.CONVO.image})`, backgroundSize: 'cover', border: '1px solid #f97316' }}>
                    <div className="icon-large">📡</div><h3 style={{ color: '#f97316' }}>PureHumCom</h3><p>Remote 2-Device Chat</p>
                </div>
                <div 
                    className="menu-card" 
                    onClick={() => {
                        if (xp >= 1500) handleGameClick("ORACLE");
                        else alert("Linguistic Oracle requires 1500 XP. Keep contributing!");
                    }} 
                    style={{ 
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${GAME_ASSETS.ORACLE.image})`, 
                        backgroundSize: 'cover', 
                        border: '1px solid #fbbf24',
                        opacity: xp >= 1500 ? 1 : 0.5,
                        cursor: xp >= 1500 ? 'pointer' : 'not-allowed'
                    }}>
                    <div className="icon-large">{xp >= 1500 ? "🔮" : "🔒"}</div>
                    <h3 style={{ color: '#fbbf24' }}>LINGUISTIC ORACLE</h3>
                    <p>{xp >= 1500 ? "Deep Cultural Context" : "Requires 1500 XP"}</p>
                </div>
                <div 
                    className="menu-card" 
                    onClick={() => {
                        if (xp >= 1500) handleGameClick("BURNWARD");
                        else alert("Burn Ward requires 1500 XP. Keep contributing!");
                    }} 
                    style={{ 
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.9)), url(${GAME_ASSETS.BURNWARD.image})`, 
                        backgroundSize: 'cover', 
                        border: '1px solid #ef4444',
                        opacity: xp >= 1500 ? 1 : 0.5,
                        cursor: xp >= 1500 ? 'pointer' : 'not-allowed'
                    }}>
                    <div className="icon-large">{xp >= 1500 ? "⚖️" : "🔒"}</div>
                    <h3 style={{ color: '#ef4444' }}>BURN WARD</h3>
                    <p>{xp >= 1500 ? "Appeal Graveyard Entries" : "Requires 1500 XP"}</p>
                </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '20px', paddingBottom: '40px' }}>
                <button
                    onClick={() => {
                        if (window.confirm("Are you sure you want to completely reset the game? This will erase your identity, XP, and progress.")) {
                            onResetGame();
                        }
                    }}
                    className="cancel-btn"
                    style={{ padding: '10px 20px', fontSize: '12px', border: '1px solid #ef4444', color: '#ef4444' }}
                >
                    ⚠️ RESET GAME DATA
                </button>
            </div>
        </div>
    );
}

// ==========================================
// 📜 GAME 1: THE ARCHIVIST 
// ==========================================
function GameArchivist({ userKey, setXP, dialects, languageHierarchy, setDialects, onBack, greeting, operator }) {
    const [mission, setMission] = useState(() => {
        const saved = localStorage.getItem(`archivist_mission_${userKey}`);
        if (saved) { try { return JSON.parse(saved); } catch (e) { } }
        return { text: greeting, subtext: "Retrieving Archive Topic...", image: getDoodleUrl("abstract") };
    });
    
    useEffect(() => {
        localStorage.setItem(`archivist_mission_${userKey}`, JSON.stringify(mission));
    }, [mission, userKey]);

    const { status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl } = useReactMediaRecorder({ audio: true });

    const loadNextTopic = async () => {
        const topics = ["Daily Life", "Food", "Weather", "Childhood", "Hobbies", "Weekend Plans"];
        const randomTopic = getRandom(topics);

        setMission(prev => ({ ...prev, text: "I appreciate you!", subtext: "Thinking of what to chat about...", image: getDoodleUrl("success") }));

        try {
            const app = await getClient();
            const res = await app.predict("/generate_mission", [randomTopic]);
            let data;
            try { data = JSON.parse(res.data[0]); } catch { data = { text: res.data[0] }; }
            setTimeout(() => {
                setMission({ text: data.text, subtext: "Journal Entry • Keep it short (1 sentence)", image: getDoodleUrl(randomTopic) });
            }, 1100);
        } catch {
            setMission({ text: `Tell me a short sentence about your ${randomTopic.toLowerCase()}.`, subtext: "Offline Mode", image: getDoodleUrl("coffee") });
        }
    };

    useEffect(() => { 
        if (mission.subtext === "Retrieving Archive Topic...") {
            loadNextTopic();
        }
    }, []);

    return (
        <SharedGameLayout
            title="ARCHIVE ENTRY" mission={mission} recStatus={status} startRec={startRecording} stopRec={stopRecording}
            mediaBlob={mediaBlobUrl} clearBlobUrl={clearBlobUrl} dialects={dialects} languageHierarchy={languageHierarchy} setDialects={setDialects} userKey={userKey} setXP={setXP}
            onBack={onBack} onReset={() => { if (window.confirm("Skip this topic?")) loadNextTopic(); }}
            onNext={loadNextTopic} sourceTag="Game: Archivist" operator={operator}
        />
    );
}

// ==========================================
// 🔮 GAME 7: LINGUISTIC ORACLE
// ==========================================
function GameLinguisticOracle({ onBack, greeting }) {
    return (
        <div className="game-container oracle-theme" style={{ borderColor: '#fbbf24', boxShadow: '0 0 30px rgba(251, 191, 36, 0.2)' }}>
            <div className="game-header">
                <button className="back-btn" onClick={onBack}>BACK</button>
                <div style={{ textAlign: 'center' }}>
                    <h2 style={{ margin: 0, color: '#fbbf24' }}>ORACLE VERIFICATION</h2>
                    <p style={{ margin: 0, fontSize: '12px', color: '#fde68a' }}>{greeting}</p>
                </div>
            </div>
            <div style={{ textAlign: 'center', padding: '30px', color: '#cbd5e1' }}>
                <h3 style={{ color: '#fbbf24' }}>PUBLIC CROWD REVIEW UNAVAILABLE</h3>
                <p>Quarantined pending contributions and audio are not exposed to public game clients.</p>
                <p>Dataset decisions require authenticated, scoped admin review.</p>
            </div>
        </div>
    );
}

// ==========================================
// ⚡ GAME 2: SPEED CHAT 
// ==========================================
function GameSpeedChat({ userKey, setXP, dialects, languageHierarchy, setDialects, onBack, greeting, operator }) {
    const [gameStage, setGameStage] = useState(() => localStorage.getItem(`speed_stage_${userKey}`) || "onboarding");
    const [nickname, setNickname] = useState(localStorage.getItem("pure_nickname") || "");
    const [currentTopic, setCurrentTopic] = useState(""); // 🟢 NEW: Tracks what they are currently talking about
    const [customTopicInput, setCustomTopicInput] = useState(""); // 🟢 NEW: Tracks what they type
    const [loading, setLoading] = useState(false);

    const [mission, setMission] = useState(() => {
        const savedMission = localStorage.getItem(`speed_mission_${userKey}`);
        if (savedMission) { try { return JSON.parse(savedMission); } catch (e) { } }
        return { text: greeting, subtext: "I'm thinking of what to ask you...", image: getDoodleUrl("network") };
    });

    const [timeLeft, setTimeLeft] = useState(10);
    const { status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl } = useReactMediaRecorder({ audio: true });

    const timerRef = useRef(null);
    const stopRecordingRef = useRef(stopRecording);

    useEffect(() => {
        if (status === "recording") {
            setTimeLeft(10);
            timerRef.current = setInterval(() => {
                setTimeLeft(prevTime => {
                    if (prevTime <= 1) {
                        clearInterval(timerRef.current);
                        // 🟢 FIX: 500ms invisible buffer to prevent audio clipping
                        setTimeout(() => {
                            stopRecordingRef.current();
                        }, 500);
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);
        } else {
            clearInterval(timerRef.current);
            setTimeLeft(10);
        }
        return () => clearInterval(timerRef.current);
    }, [status]);

    const fetchMission = async (topic) => {
        setLoading(true);
        setMission(prev => ({ ...prev, subtext: `Establishing Neural Link for: ${topic}...` }));
        try {
            // 🟢 FIX: Directly connect to the cloud space to ensure it never fails on the first load
            const app = await getClient();

            // 🟢 FIX 1: Increased timeout from 4.5s to 8s to allow the LLM time to generate
            const timeoutPromise = new Promise((_, r) => setTimeout(() => r(new Error("Timeout")), 8000));

            // 🟢 FIX 2: Clearer prompt instructions for the AI
            const prompt = `Topic: ${topic}. Ask a short, engaging 1-sentence question about this topic for a player named ${nickname}.`;
            const apiPromise = app.predict("/generate_mission", [prompt]);

            const res = await Promise.race([apiPromise, timeoutPromise]);

            let data;
            try { data = JSON.parse(res.data[0]); } catch { data = { text: res.data[0] }; }

            // Clean up any stray quotation marks the AI might add
            let cleanText = typeof data.text === 'string' ? data.text.replace(/^["']|["']$/g, '') : data.text;

            setMission({ text: cleanText, subtext: `Target: ${topic} • 10 Seconds`, image: getDoodleUrl(topic) });
            setGameStage("mission");
        } catch (e) {
            // 🟢 FIX 3: Smart contextual fallback that actually makes sense!
            let fallbackText = "";

            if (topic === "General") {
                // If the topic is General, grab a random human-sounding question
                const randomStarter = STARTER_QUESTIONS[Math.floor(Math.random() * STARTER_QUESTIONS.length)];
                fallbackText = `Hey ${nickname}, here's a random one: ${randomStarter}`;
            } else {
                // If it's a specific topic, use it in the sentence
                fallbackText = `Hey ${nickname}, What do you like about ${topic.toLowerCase()} the most?`;
            }

            setMission({ text: fallbackText, subtext: `Local Backup: ${topic} • 10 Seconds`, image: getDoodleUrl(topic) });
            setGameStage("mission");
        } finally { setLoading(false); }
    };

    // 🟢 FIX: Start round with their custom topic, or default to "General"
    const startFirstRound = () => {
        if (nickname) {
            localStorage.setItem("pure_nickname", nickname);
            const startingTopic = customTopicInput.trim() || "General";
            setCurrentTopic(startingTopic);
            setCustomTopicInput(""); // Clear the input
            fetchMission(startingTopic);
        }
    };

    // 🟢 FIX: Reusable handler for clicking OR typing a topic
    const handleTopicSelection = (topic) => {
        const selected = topic.trim() || "General";
        setCurrentTopic(selected);
        setCustomTopicInput(""); // Clear the input
        fetchMission(selected);
    };

    // 🟢 NEW: Instead of going to the menu, we generate a contextual follow-up question
    const handleNextRound = (userUtterance, userClarification) => {
        const lastAnswer = userClarification || userUtterance || "something interesting";
        fetchFollowUp(currentTopic, lastAnswer);
    };

    const fetchFollowUp = async (topic, lastAnswer) => {
        setLoading(true);
        setMission(prev => ({ ...prev, subtext: `Analyzing response...` }));
        try {
            const app = await getClient(); // 🟢 FIX: Directly connect so the AI doesn't fail!
            const timeoutPromise = new Promise((_, r) => setTimeout(() => r(new Error("Timeout")), 8000));

            // 🟢 NEW: Tell Gemini exactly what the user just said so it can dig deeper
            const prompt = `Topic: ${topic}. Player Name: ${nickname}. The player just answered a question by saying: "${lastAnswer}". Ask a short, engaging 1-sentence follow-up question asking WHY or asking for more details.`;

            const apiPromise = app.predict("/generate_mission", [prompt]);
            const res = await Promise.race([apiPromise, timeoutPromise]);

            let data;
            try { data = JSON.parse(res.data[0]); } catch { data = { text: res.data[0] }; }
            let cleanText = typeof data.text === 'string' ? data.text.replace(/^["']|["']$/g, '') : data.text;

            setMission({ text: cleanText, subtext: `Follow-up: ${topic} • 10 Seconds`, image: getDoodleUrl(topic) });
        } catch (e) {
            setMission({ text: `That's interesting, ${nickname}! Tell me more about why you feel that way.`, subtext: `Local Backup: ${topic} • 10 Seconds`, image: getDoodleUrl(topic) });
        } finally { setLoading(false); }
    };

    const handleReset = () => {
        if (window.confirm("Reset Speed Chat identity?")) {
            localStorage.removeItem("pure_nickname"); localStorage.removeItem(`speed_stage_${userKey}`);
            setNickname(""); setGameStage("onboarding");
        }
    };

    if (gameStage === "onboarding" && !localStorage.getItem("pure_nickname")) {
        return (
            <div className="game-layout">
                <div className="mission-card phc-setup-card" style={{ justifyContent: 'center', alignItems: 'center' }}>
                    <div className="icon-large">🕵️‍♂️</div>
                    <h3 style={{ color: '#ffffff', textShadow: '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 0px 4px 10px rgba(0,0,0,0.9)', fontSize: '18px' }}>WHAT'S YOUR NICKNAME?</h3>
                    <input className="cyber-input" value={nickname} onChange={e => setNickname(e.target.value)} placeholder="e.g. Maverick" style={{ margin: '10px 0 20px', textAlign: 'center' }} />

                    {/* 🟢 NEW: Ask for the initial topic */}
                    <h3 style={{ color: '#38bdf8', textShadow: '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 0px 4px 10px rgba(0,0,0,0.9)', fontSize: '14px' }}>WHAT DO YOU WANT TO TALK ABOUT?</h3>
                    <input className="cyber-input" value={customTopicInput} onChange={e => setCustomTopicInput(e.target.value)} placeholder="e.g. Tech, Food, Anime (Optional)" style={{ margin: '10px 0 20px', textAlign: 'center' }} />

                    <div className="action-row"><button className="cancel-btn" onClick={onBack}>BACK</button><button className="cyber-button" onClick={startFirstRound} disabled={!nickname}>START MISSION</button></div>
                </div>
            </div>
        );
    }

    if (gameStage === "topic_select") {
        return (
            <div className="game-layout">
                <div className="mission-card phc-setup-card" style={{ display: 'block' }}>
                    <h3 style={{ color: '#ffffff', textShadow: '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 0px 4px 10px rgba(0,0,0,0.9)', marginBottom: '15px' }}>🔄 MISSION COMPLETE</h3>

                    {/* 🟢 NEW: Option to continue the current conversation branch */}
                    {currentTopic && (
                        <div style={{ background: 'rgba(0,0,0,0.5)', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #facc15' }}>
                            <p style={{ fontSize: '13px', color: '#facc15', fontWeight: 'bold', margin: '0 0 10px 0', textShadow: '1px 1px 2px #000' }}>Keep talking about "{currentTopic}"?</p>
                            <button className="cyber-button" style={{ background: '#facc15', color: '#000', width: '100%', padding: '10px' }} onClick={() => handleTopicSelection(currentTopic)}>
                                💬 CONTINUE TOPIC
                            </button>
                        </div>
                    )}

                    <p style={{ fontSize: '12px', color: '#cbd5e1', fontWeight: 'bold', marginBottom: '10px' }}>Or switch to a new topic:</p>

                    {/* 🟢 NEW: Input to type a completely custom topic */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
                        <input className="cyber-input" value={customTopicInput} onChange={e => setCustomTopicInput(e.target.value)} placeholder="Type custom topic..." style={{ flex: 1, margin: 0 }} />
                        <button className="cyber-button" onClick={() => handleTopicSelection(customTopicInput)} disabled={!customTopicInput.trim()} style={{ padding: '0 20px', background: '#38bdf8', color: '#000', margin: 0 }}>GO</button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {[...TOPIC_SUGGESTIONS].sort(() => 0.5 - Math.random()).slice(0, 4).map(t => (
                            <button
                                key={t}
                                className="cyber-button"
                                style={{
                                    background: 'rgba(15, 23, 42, 0.9)',
                                    border: '1px solid #f472b6',
                                    color: '#ffffff',
                                    fontWeight: 'bold',
                                    textShadow: '1px 1px 2px #000',
                                    fontSize: '12px',
                                    padding: '12px'
                                }}
                                onClick={() => handleTopicSelection(t)}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                    <button onClick={handleReset} style={{ marginTop: '20px', background: 'transparent', border: '1px dotted #ef4444', color: '#ef4444', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', width: '100%' }}>🚫 RESET IDENTITY</button>
                </div>
            </div>
        );
    }

    if (loading) return <div className="game-layout"><div className="loading">GENERATING MISSION...</div></div>;

    return (
        <SharedGameLayout
            title={`CHAT: ${nickname.toUpperCase()}`} mission={mission} recStatus={status} startRec={startRecording} stopRec={stopRecording}
            mediaBlob={mediaBlobUrl} clearBlobUrl={clearBlobUrl} dialects={dialects} languageHierarchy={languageHierarchy} setDialects={setDialects} userKey={userKey} operator={operator} setXP={setXP}
            onBack={onBack} timer={timeLeft} onNext={handleNextRound} onReset={handleReset} onChangeTopic={() => setGameStage("topic_select")} sourceTag="Game: SpeedChat"
        />
    );
}

// ==========================================
// 👁️ GAME 3: VISION QUEST 
// ==========================================
function GameVisionQuest({ userKey, setXP, dialects, languageHierarchy, setDialects, onBack, greeting, operator }) {
    const [mission, setMission] = useState(() => {
        const saved = localStorage.getItem(`vision_mission_${userKey}`);
        if (saved) { try { return JSON.parse(saved); } catch (e) { } }
        return { text: greeting, subtext: "Calibrating Optical Sensors...", image: getPhotoUrl("cyberpunk") };
    });
    
    useEffect(() => {
        localStorage.setItem(`vision_mission_${userKey}`, JSON.stringify(mission));
    }, [mission, userKey]);

    const { status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl } = useReactMediaRecorder({ audio: true });

    const loadNewImage = () => {
        const keywords = ["market", "street", "festival", "classroom", "kitchen"];
        const k = getRandom(keywords);
        setMission({ text: "In your own way, describe the first thing you see in this image.", subtext: `Target Sector: ${k.toUpperCase()}`, image: getPhotoUrl(k) });
    };

    useEffect(() => { 
        if (mission.subtext === "Calibrating Optical Sensors...") {
            setTimeout(loadNewImage, 2500); 
        }
    }, []);

    return (
        <SharedGameLayout
            title="OBSERVATION DECK" mission={mission} recStatus={status} startRec={startRecording} stopRec={stopRecording}
            mediaBlob={mediaBlobUrl} clearBlobUrl={clearBlobUrl} dialects={dialects} languageHierarchy={languageHierarchy} setDialects={setDialects} userKey={userKey} setXP={setXP} onBack={onBack}
            onNext={loadNewImage} mode="vision" sourceTag="Game: Vision" operator={operator}
        />
    );
}

// ==========================================
// 👂 GAME 4: THE LISTENER 
// ==========================================
function GameActiveListener({ userKey, setXP, dialects, languageHierarchy, setDialects, onBack, operator }) {
    const inferenceMetadataRef = useRef({});
    const loadSavedData = () => readExpiringConversationState("echo_memory_" + userKey);
    const memory = loadSavedData();
    const [setup, setSetup] = useState(memory ? memory.setup : { voiceIndex: 0, pitch: 1.0, rate: 1.0, userLanguage: "English", userDialect: dialects[0] || "General" });
    const [messages, setMessages] = useState(memory ? memory.messages : []);
    const [nickname, setNickname] = useState(memory ? memory.nickname : "");
    const [pronunciation, setPronunciation] = useState(memory ? memory.pronunciation : "");
    const [phase, setPhase] = useState(memory ? "chat" : "setup");
    const [voices, setVoices] = useState([]);
    const [currentAudio, setCurrentAudio] = useState(null);
    const [currentTranscript, setCurrentTranscript] = useState("");
    const [currentClarification, setCurrentClarification] = useState("");
    const [isEdited, setIsEdited] = useState(false);
    const [showPhonetic, setShowPhonetic] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [contributeCurrentTurn, setContributeCurrentTurn] = useState(false);
    const chatEndRef = useRef(null);
    const listenerMachineTranscriptRef = useRef("");
    const listenerMachineOutputRef = useRef("");
    const listenerTaskIdRef = useRef("");
    const listenerTurnIdRef = useRef("");
    const listenerStartedAtRef = useRef("");
    const listenerConversationIdRef = useRef(memory?.conversationId || "");
    if (!listenerConversationIdRef.current) listenerConversationIdRef.current = createCollectionId("conversation");
    const listenerPendingContributionRef = useRef(null);
    const listenerConfirmingRef = useRef(false);

    const [customD, setCustomD] = useState("");
    const [captcha, setCaptcha] = useState("");
    const [captchaMath, setCaptchaMath] = useState({ a: 3, b: 4 });

    useEffect(() => {
        if (setup.userDialect === "+ Add New Dialect") {
            setCaptchaMath({
                a: Math.floor(Math.random() * 10) + 1,
                b: Math.floor(Math.random() * 10) + 1
            });
            setCaptcha("");
        }
    }, [setup.userDialect]);

    const availableLanguages = Object.keys(languageHierarchy || { English: dialects });
    const availableDialects = languageHierarchy?.[setup.userLanguage] || [setup.userDialect];
    
    // Auto-select first dialect when language changes
    const handleLanguageChange = (e) => {
        const newLang = e.target.value;
        const newDialects = languageHierarchy?.[newLang] || (newLang === "English" ? dialects : []);
        setSetup({ ...setup, userLanguage: newLang, userDialect: newDialects[0] || "" });
    };

    // 🟢 NEW: Fetch XP specifically for the selected dialect in The Listener
    useEffect(() => {
        const fetchXP = async () => {
            const d = setup.userDialect;
            if (!d || d === "+ Add New Dialect") return;

            try {
                const app = await getClient();
                const res = await app.predict("/get_user_xp", [operator, d]);
                const newXp = JSON.parse(res.data[0]).xp;
                mergeDialectXp(setXP, d, newXp);
            } catch(e) {
                console.error("XP Sync Error", e);
            }
        };
        fetchXP();
    }, [setup.userDialect, operator, setXP]);

    useEffect(() => {
        if (phase === "chat" || phase === "verify") {
            const dataToSave = { setup, messages, nickname, pronunciation, conversationId: listenerConversationIdRef.current };
            writeExpiringConversationState("echo_memory_" + userKey, dataToSave);
        }
    }, [messages, setup, nickname, pronunciation, phase, userKey]);

    const memoryRef = useRef(memory);
    useEffect(() => {
        const loadVoices = () => {
            const all = window.speechSynthesis.getVoices();
            if (all.length > 0) {
                const englishVoices = all.filter(v => v.lang.startsWith("en"));
                setVoices(englishVoices);
                if (!memoryRef.current) {
                    const defaultVoice = englishVoices.findIndex(v => v.name.includes("Google US English") || v.name.includes("Samantha"));
                    setSetup(prev => ({ ...prev, voiceIndex: defaultVoice !== -1 ? defaultVoice : 0 }));
                }
            }
        };
        loadVoices();
        window.speechSynthesis.onvoiceschanged = loadVoices;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, phase]);

    const handleReset = () => { if (window.confirm("Start a new conversation?")) { localStorage.removeItem("echo_memory_" + userKey); listenerConversationIdRef.current = createCollectionId("conversation"); listenerPendingContributionRef.current = null; setMessages([]); setNickname(""); setPronunciation(""); setPhase("setup"); } };

    const speak = (text, forcePronunciation = null) => {
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
            let spokenText = text;
            const targetName = nickname || currentTranscript;
            const targetSound = forcePronunciation || pronunciation;
            if (targetName && targetSound) { const regex = new RegExp(targetName, "gi"); spokenText = text.replace(regex, targetSound); }
            const u = new SpeechSynthesisUtterance(spokenText);
            const selectedVoice = voices[setup.voiceIndex];
            if (selectedVoice) u.voice = selectedVoice;
            u.lang = "en-US"; // Listener intentionally has English replies.
            u.pitch = setup.pitch; u.rate = setup.rate;
            window.speechSynthesis.speak(u);
        }
    };

    const startSession = () => {
        if (setup.userDialect === "+ Add New Dialect") {
            if (!customD.trim()) { alert("Please type a new dialect name."); return; }
            if (parseInt(captcha) !== (captchaMath.a + captchaMath.b)) {
                alert(`Bot Check Failed! ${captchaMath.a} + ${captchaMath.b} = ${captchaMath.a + captchaMath.b}.`);
                return;
            }
            setSetup({ ...setup, userDialect: customD });

            setDialects(prev => {
                const clean = prev.filter(d => d !== "+ Add New Dialect");
                return Array.from(new Set([customD, ...clean, "+ Add New Dialect"]));
            });
        }
        try { resolveLanguage(setup.userLanguage, setup.customLanguage); } catch (error) { window.alert(error.message); return; }
        const intro = "Hello, my name is Echo. What should I call you?";
        setMessages([{ sender: 'ai', text: intro }]); setPhase("onboarding"); speak(intro);
    };

    const handleRetry = () => {
        listenerPendingContributionRef.current = null;
        setPhase("chat"); setCurrentTranscript(""); setCurrentClarification(""); setIsEdited(false);
        listenerMachineTranscriptRef.current = "";
        listenerMachineOutputRef.current = "";
        listenerTaskIdRef.current = createCollectionId("task");
        listenerTurnIdRef.current = createCollectionId("turn");
        listenerStartedAtRef.current = new Date().toISOString();
    };

    const handleRegenerateMeaning = async () => {
        if (!currentTranscript.trim()) return;
        setCurrentClarification("");
        setIsRegenerating(true);
        try {
            const app = await getClient();
            const d = setup.userDialect || "General";
            const cRes = await clarifyText(app, currentTranscript, resolveLanguage(setup.userLanguage, setup.customLanguage), d);
            inferenceMetadataRef.current = cRes.inference;
            let cText = cRes.data[0];
            try { cText = JSON.parse(cText).clarification || cText; } catch { }
            setCurrentClarification(cText); setIsRegenerating(false);
            if (!listenerMachineOutputRef.current) listenerMachineOutputRef.current = cText;
        } catch (e) { setCurrentClarification(""); window.alert("Meaning generation failed. Text is limited to 4,000 characters; please retry."); setIsRegenerating(false); }
    };

    const handleSubmitName = () => {
        if (!currentTranscript.trim()) return;
        const name = currentTranscript.trim(); const sound = pronunciation.trim() || name;
        setNickname(name); setPronunciation(sound);
        const response = `Nice to meet you, ${name}. What would you like to talk about?`;
        setMessages(p => [...p, { sender: 'user', text: name }, { sender: 'ai', text: response }]);
        speak(response, sound); setCurrentTranscript(""); setPhase("chat");
    };

    const handleConfirm = async () => {
        if (listenerConfirmingRef.current) return;
        if (!validMeaning(currentClarification) || isRegenerating) { window.alert("Generate or enter a valid reviewed meaning first."); return; }
        const shouldContribute = contributeCurrentTurn;
        listenerConfirmingRef.current = true;
        let keepReview = true;
        setPhase("responding");
        try {
            const d = setup.userDialect || "General";
            const clarSource = isEdited ? "User/AI Hybrid" : "AI";
            const submittedAt = new Date().toISOString();
            const startedAt = listenerStartedAtRef.current || (listenerStartedAtRef.current = submittedAt);
            const taskId = listenerTaskIdRef.current || (listenerTaskIdRef.current = createCollectionId("task"));
            const turnId = listenerTurnIdRef.current || (listenerTurnIdRef.current = createCollectionId("turn"));
            const promptText = [...messages].reverse().find(message => message.sender === "ai")?.text || "";
            if (shouldContribute) {
                try {
                    await submitReviewedListenerContribution(listenerPendingContributionRef, {
                participant_id: getCollectionParticipantId(userKey, "pureversation"),
                app_id: APP_IDENTITY.name,
                app_version: APP_VERSION,
                game_id: "listener",
                task_id: taskId,
                prompt_text: promptText,
                topic_id: "",
                image_asset_id: "",
                conversation_id: listenerConversationIdRef.current,
                turn_id: turnId,
                preceding_context: "",
                source_language: resolveLanguage(setup.userLanguage, setup.customLanguage),
                source_dialect: d,
                target_language: "English",
                target_dialect: "Standard English",
                input_modality: currentAudio ? "voice" : "text",
                source_text: currentTranscript,
                machine_transcript_initial: listenerMachineTranscriptRef.current,
                user_transcript_final: currentTranscript,
                machine_output_initial: listenerMachineOutputRef.current,
                user_output_final: currentClarification,
                asr_model: currentAudio ? "Whisper tiny" : "",
                model_name: inferenceMetadataRef.current.model || "",
                model_version: "",
                translation_route: inferenceMetadataRef.current.route || "",
                started_at: startedAt,
                submitted_at: submittedAt,
                duration_ms: Math.max(0, Date.parse(submittedAt) - Date.parse(startedAt)),
                consent_version: COLLECTION_CONSENT_VERSION,
                metadata: { source_tag: "Game: Listener", clarification_source: clarSource },
                    }, submitCollectionSubmission);
                } catch (contributionError) {
                    window.alert(listenerContributionFailureMessage(contributionError));
                    return;
                }
            }

            keepReview = false;
            setContributeCurrentTurn(false);
            setMessages(p => [...p, { sender: 'user', text: currentTranscript, isAudio: !isEdited }]);
            const app = await getClient();
            const responseText = await replyText(app, currentTranscript, currentClarification, resolveLanguage(setup.userLanguage, setup.customLanguage), d, "English", "Standard English");
            setMessages(p => [...p, { sender: 'ai', text: responseText }]);
            speak(responseText);
            awardAccountXp(setXP, 25, setup.userDialect || "General");
        } catch (e) {
            console.warn("Listener response unavailable");
            window.alert("Echo is unavailable. No AI reply was generated; please retry.");
        } finally {
            listenerConfirmingRef.current = false;
            if (keepReview) {
                setPhase("verify");
            } else {
                listenerPendingContributionRef.current = null;
                setPhase("chat"); setCurrentTranscript(""); setCurrentClarification(""); setIsEdited(false);
                listenerMachineTranscriptRef.current = "";
                listenerMachineOutputRef.current = "";
                listenerTaskIdRef.current = createCollectionId("task");
                listenerTurnIdRef.current = createCollectionId("turn");
                listenerStartedAtRef.current = new Date().toISOString();
            }
        }
    };

    const handleAudioStop = async (blobUrl, blob) => {
        if (phase === "onboarding") {
            setPhase("processing");
            try {
                const app = await getClient();
                const d = setup.userDialect || "General";
                const tRes = await app.predict("/transcribe_check", [wrapAudioBlobForUpload(await alterVoiceForCloudUpload(blob)), asrHint(resolveLanguage(setup.userLanguage, setup.customLanguage), d)]);
                const text = tRes.data[0];
                setCurrentTranscript(text); if (!pronunciation) setPronunciation(text); setPhase("onboarding");
            } catch (e) { setPhase("onboarding"); }
            return;
        }
        setPhase("processing"); setCurrentAudio(blob); setCurrentClarification(""); setIsEdited(false);
        listenerPendingContributionRef.current = null;
        listenerTaskIdRef.current = createCollectionId("task");
        listenerTurnIdRef.current = createCollectionId("turn");
        listenerStartedAtRef.current = new Date().toISOString();
        try {
            const app = await getClient();
            const d = setup.userDialect || "General";
            const tRes = await app.predict("/transcribe_check", [wrapAudioBlobForUpload(await alterVoiceForCloudUpload(blob)), asrHint(resolveLanguage(setup.userLanguage, setup.customLanguage), d)]);
            const text = tRes.data[0];
            setCurrentTranscript(text);
            listenerMachineTranscriptRef.current = text;
            const cRes = await clarifyText(app, text, resolveLanguage(setup.userLanguage, setup.customLanguage), d);
            inferenceMetadataRef.current = cRes.inference;
            let cText = cRes.data[0];
            try { cText = JSON.parse(cText).clarification || cText; } catch { }
            setCurrentClarification(cText); listenerMachineOutputRef.current = cText; setPhase("verify");
        } catch (e) { setCurrentClarification(""); window.alert("Transcription or meaning generation failed. Please retry."); setPhase("chat"); }
    };

    const { status, startRecording, stopRecording, mediaBlobUrl } = useReactMediaRecorder({ audio: true, onStop: handleAudioStop });

    return (
        <div className="game-layout listener-mode">
            <p role="note">Source language controls transcription and meaning. Echo replies in English.</p>
            <div className="listener-header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <button className="back-icon" onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '20px', padding: '0' }} title="Back to Menu">🏠←</button>
                <div className={`status-dot ${status === "recording" ? "pulsing-red" : ""}`}></div>
                <h3 style={{ margin: 0, color: '#f8fafc', flex: 1 }}>ECHO {nickname ? `| ${nickname.toUpperCase()}` : ""}</h3>

                {/* 🟢 FIX: Exact same Reset button styling as SharedGameLayout */}
                <button onClick={handleReset} style={{ background: 'rgba(0,0,0,0.3)', border: 'none', borderRadius: '50%', width: '40px', height: '30px', cursor: 'pointer', fontSize: '16px', color: '#94a3b8', flexShrink: 0 }} title="Reset Identity">🗑️</button>
            </div>
            {phase === "setup" ? (
                <div className="setup-screen">
                    <div className="icon-large">🎛️</div><h3>CONFIGURE ECHO</h3>
                    <div className="setup-row"><label>YOUR LANGUAGE</label><select value={setup.userLanguage} onChange={handleLanguageChange}>{availableLanguages.map(l => <option key={l} value={l}>{l}</option>)}</select></div>
                    {setup.userLanguage === "+ Add New Language" && (
                        <div className="setup-row"><input className="cyber-input" placeholder="New Language Name" value={setup.customLanguage || ""} onChange={e => setSetup({ ...setup, customLanguage: e.target.value })} style={{ marginBottom: '10px', width: '90%' }} /></div>
                    )}
                    <div className="setup-row"><label>YOUR DIALECT</label><select value={setup.userDialect} onChange={e => setSetup({ ...setup, userDialect: e.target.value })}>{availableDialects.map(d => <option key={d} value={d}>{d}</option>)}</select></div>

                    {setup.userDialect === "+ Add New Dialect" && (
                        <div className="setup-row" style={{ background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '8px', border: '1px dotted #38bdf8' }}>
                            <input className="cyber-input" placeholder="New Dialect Name" value={customD} onChange={e => setCustomD(e.target.value)} style={{ marginBottom: '10px', width: '90%' }} />
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Human Check: {captchaMath.a} + {captchaMath.b} =</span>
                                <input className="cyber-input" style={{ width: '50px', textAlign: 'center' }} type="number" value={captcha} onChange={e => setCaptcha(e.target.value)} />
                            </div>
                        </div>
                    )}

                    <div className="setup-row"><label>AI VOICE</label><select value={setup.voiceIndex} onChange={e => setSetup({ ...setup, voiceIndex: parseInt(e.target.value) })}>{voices.map((v, i) => <option key={i} value={i}>{v.name} ({v.lang})</option>)}</select></div>
                    <div className="setup-row"><label>PITCH ({setup.pitch}x)</label><input type="range" min="0.5" max="2.0" step="0.1" value={setup.pitch} onChange={e => setSetup({ ...setup, pitch: parseFloat(e.target.value) })} /></div>
                    <div className="setup-row"><label>SPEED ({setup.rate}x)</label><input type="range" min="0.5" max="2.0" step="0.1" value={setup.rate} onChange={e => setSetup({ ...setup, rate: parseFloat(e.target.value) })} /></div>
                    <button className="cyber-button" onClick={() => speak("Voice check.")} style={{ marginBottom: '10px', background: 'transparent', border: '1px solid #38bdf8' }}>🔊 TEST</button>
                    <button className="cyber-button" onClick={startSession}>INITIALIZE SYSTEM</button>
                </div>
            ) : (
                <>
                    <div className="chat-log">
                        {messages.map((m, i) => (<div key={i} className={`chat-bubble ${m.sender}`}>{m.isAudio && <span style={{ marginRight: '5px' }}>🎤</span>}{m.text}</div>))}
                        {phase === "processing" && <div className="chat-bubble ai typing">Echo is thinking...</div>}{phase === "responding" && <div className="chat-bubble ai typing">Echo is replying...</div>}<div ref={chatEndRef} />
                    </div>
                    <div className="listener-controls">
                        {phase === "onboarding" && (
                            <div className="onboarding-controls">
                                <label style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>SAY OR TYPE YOUR NAME:</label>
                                <div className="input-bar" style={{ marginBottom: '10px' }}>
                                    <input className="cyber-input" value={currentTranscript} onChange={e => { setCurrentTranscript(e.target.value); if (!showPhonetic) setPronunciation(e.target.value); }} placeholder="Display Name (e.g. Onyi)" onKeyDown={(e) => e.key === 'Enter' && handleSubmitName()} />
                                </div>
                                {showPhonetic && (<div className="input-bar" style={{ marginBottom: '10px', animation: 'fadeIn 0.3s' }}><label style={{ fontSize: '9px', color: '#38bdf8', marginRight: '10px' }}>PHONETIC SPELLING:</label><input className="cyber-input" value={pronunciation} onChange={e => setPronunciation(e.target.value)} placeholder="e.g. Own-yee" /></div>)}
                                <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                                    {currentTranscript && (<button onClick={() => speak(currentTranscript, pronunciation || currentTranscript)} style={{ background: 'transparent', border: '1px solid #38bdf8', color: '#38bdf8', borderRadius: '8px', padding: '0 10px', fontSize: '12px' }}>🔊 HEAR IT</button>)}
                                    {currentTranscript && !showPhonetic && (<button onClick={() => { setShowPhonetic(true); setPronunciation(currentTranscript) }} style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '10px', textDecoration: 'underline' }}>Wrong Pronunciation?</button>)}
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <button className={`record-btn ${status === "recording" ? "pulsing" : ""}`} onPointerDown={startRecording} onPointerUp={stopRecording} onPointerLeave={stopRecording} style={{ width: '100%', borderRadius: '8px', fontSize: '16px', padding: '10px', userSelect: 'none', touchAction: 'none' }}>{status === "recording" ? "🔴" : "🎙️"}</button>
                                        <div style={{ textAlign: 'center', fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>Hold to record</div>
                                    </div>
                                    <button className="cyber-button" onClick={handleSubmitName} style={{ flex: 1, background: currentTranscript ? '#22c55e' : '#334155', alignSelf: 'flex-start', padding: '12px' }} disabled={!currentTranscript}>CONFIRM ✅</button>
                                </div>
                            </div>
                        )}
                        {(phase === "chat" || phase === "processing" || phase === "responding") && (
                            <div className="record-bar">
                                <button className={`record-btn ${status === "recording" ? "pulsing" : ""}`} onPointerDown={startRecording} onPointerUp={stopRecording} onPointerLeave={stopRecording} disabled={phase !== "chat"} style={{ width: '100%', borderRadius: '12px', padding: '20px', fontSize: '20px', userSelect: 'none', touchAction: 'none' }}>{status === "recording" ? "🔴" : phase === "processing" ? "⏳" : phase === "responding" ? "🔊" : "🎙️"}</button>
                                <div style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', marginTop: '5px' }}>{status === "recording" ? "Release to send" : phase === "processing" ? "Processing..." : phase === "responding" ? "Speaking..." : "Hold to record"}</div>
                            </div>
                        )}
                        {phase === "verify" && (
                            <div className="verify-card">
                                <div className="audio-preview" style={{ marginBottom: '15px', background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '12px', border: '1px solid #334155' }}>
                                    <label style={{ fontSize: '10px', color: '#94a3b8', display: 'block', marginBottom: '5px', letterSpacing: '1px' }}>REVIEW YOUR AUDIO:</label>
                                    <audio src={mediaBlobUrl} controls style={{ width: '100%', height: '35px', borderRadius: '8px', outline: 'none' }} />
                                </div>

                                <div className="verify-row">
                                    <label className="hitl-label">I HEARD (TYPE TO EDIT):</label>
                                    <textarea className="hitl-edit-box" value={currentTranscript} onChange={e => { setCurrentTranscript(e.target.value); setIsEdited(true); }} style={{ border: isEdited ? '2px solid #22c55e !important' : '' }} />
                                </div>
                                <div className="verify-row">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                        <label className="hitl-label" style={{ margin: 0 }}>MEANING (EDIT IF WRONG):</label>
                                        <button className="hitl-action-btn" onClick={handleRegenerateMeaning} disabled={isRegenerating}>
                                            {isRegenerating ? "UPDATING..." : "🔄 UPDATE MEANING"}
                                        </button>
                                    </div>
                                    <textarea className="hitl-edit-box" value={currentClarification} onChange={e => { setCurrentClarification(e.target.value); setIsEdited(true); }} />
                                </div>
                                <div className="verify-actions">
                                    <button className="reject-btn" onClick={handleRetry} style={{ background: '#334155', border: 'none', color: '#ef4444' }}>🎤 RETRY</button>
                                    <button className="confirm-btn" onClick={handleConfirm} disabled={!validMeaning(currentClarification) || isRegenerating}>{isEdited ? "SEND EDIT ✅" : "CONFIRM ✅"}</button>
                                </div>
                                <label style={{ display: 'flex', gap: '8px', marginTop: '10px', fontSize: '11px', color: '#cbd5e1' }}>
                                    <input type="checkbox" checked={contributeCurrentTurn} onChange={event => setContributeCurrentTurn(event.target.checked)} />
                                    Also contribute this reviewed turn to the moderated research queue (off by default).
                                </label>
                            </div>
                        )}

                        {/* 🟢 FIX: Call the CustomEvent to open the Feedback Modal */}
                        <div style={{ textAlign: 'center', marginTop: '15px', paddingBottom: '10px' }}>
                            <button onClick={() => window.dispatchEvent(new CustomEvent('open-feedback'))} style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer' }}>
                                💬 Feedback (Unavailable)
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// ==========================================
// 🌉 GAME 5: CONVO BRIDGE (Turn-Based Live Translation)
// ==========================================
function GameConvoBridge({ userKey, dialects, languageHierarchy, onBack, setXP, operator }) {
    const [p1Language, setP1Language] = useState(() => localStorage.getItem(`bridge_p1_lang_${userKey}`) || "English");
    const [p2Language, setP2Language] = useState(() => localStorage.getItem(`bridge_p2_lang_${userKey}`) || "English");
    const [p1Dialect, setP1Dialect] = useState(() => localStorage.getItem(`bridge_p1_${userKey}`) || dialects[0] || "General English");
    const [p2Dialect, setP2Dialect] = useState(() => localStorage.getItem(`bridge_p2_${userKey}`) || (dialects.length > 1 ? dialects[1] : "General English"));
    const [chatLog, setChatLog] = useState(() => {
        return readExpiringConversationState(`bridge_log_${userKey}`) || [];
    });

    useEffect(() => {
        localStorage.setItem(`bridge_p1_lang_${userKey}`, p1Language);
        localStorage.setItem(`bridge_p2_lang_${userKey}`, p2Language);
        localStorage.setItem(`bridge_p1_${userKey}`, p1Dialect);
        localStorage.setItem(`bridge_p2_${userKey}`, p2Dialect);
        writeExpiringConversationState(`bridge_log_${userKey}`, chatLog);
    }, [p1Language, p2Language, p1Dialect, p2Dialect, chatLog, userKey]);

    const availableLanguages = Object.keys(languageHierarchy || { English: dialects });
    const p1AvailableDialects = languageHierarchy?.[p1Language] || [p1Dialect];
    const p2AvailableDialects = languageHierarchy?.[p2Language] || [p2Dialect];

    const handleP1LangChange = (e) => {
        const newLang = e.target.value;
        setP1Language(newLang);
        const newDialects = languageHierarchy?.[newLang] || (newLang === "English" ? dialects : []);
        setP1Dialect(newDialects[0] || "");
    };

    const handleP2LangChange = (e) => {
        const newLang = e.target.value;
        setP2Language(newLang);
        const newDialects = languageHierarchy?.[newLang] || (newLang === "English" ? dialects : []);
        setP2Dialect(newDialects[0] || "");
    };

    // 🟢 NEW: Fetch XP specifically for the selected dialect in The Bridge
    useEffect(() => {
        const fetchXP = async () => {
            const d = p1Dialect; // The user's dialect in this scenario
            if (!d || d === "+ Add New Dialect") return;

            try {
                const app = await getClient();
                const res = await app.predict("/get_user_xp", [operator, d]);
                const newXp = JSON.parse(res.data[0]).xp;
                mergeDialectXp(setXP, d, newXp);
            } catch(e) {
                console.error("XP Sync Error", e);
            }
        };
        fetchXP();
    }, [p1Dialect, operator, setXP]);
    const [isProcessing, setIsProcessing] = useState(false);
    const speak = (text, language, dialect) => speakSelectedLanguage(text, language, dialect);

    const handleTurn = async (blobUrl, blob, speaker) => {
        setIsProcessing(true);
        const sourceLanguage = speaker === "P1" ? p1Language : p2Language;
        const targetLanguage = speaker === "P1" ? p2Language : p1Language;
        const sourceDialect = speaker === "P1" ? p1Dialect : p2Dialect;
        const targetDialect = speaker === "P1" ? p2Dialect : p1Dialect;

        try {
            const app = await getClient(); // Using your singleton client

            // 1. Transcribe the audio
            const tRes = await app.predict("/transcribe_check", [wrapAudioBlobForUpload(await alterVoiceForCloudUpload(blob)), asrHint(sourceLanguage, sourceDialect)]);
            const originalText = tRes.data[0];

            // 2. Translate to the other player's dialect
            const translatedText = await nativeTranslate(originalText, targetDialect, sourceDialect, targetLanguage, sourceLanguage);

            // 3. Update the chat log
            setChatLog(prev => [...prev, {
                sender: speaker,
                original: originalText,
                translated: translatedText,
                sourceLanguage, targetLanguage, sourceD: sourceDialect,
                targetD: targetDialect
            }]);

            // 4. Read it out loud to the other player
            speak(translatedText, targetLanguage, targetDialect);

        } catch (e) {
            console.error("Translation failed:", e); window.alert("Translation unavailable. Nothing was sent or spoken.");
        } finally {
            setIsProcessing(false);
        }
    };

    const [p1Text, setP1Text] = useState('');
    const [p2Text, setP2Text] = useState('');

    const handleTextTurn = async (text, speaker) => {
        if (!text.trim()) return;
        setIsProcessing(true);
        const sourceLanguage = speaker === 'P1' ? p1Language : p2Language;
        const targetLanguage = speaker === 'P1' ? p2Language : p1Language;
        const sourceDialect = speaker === 'P1' ? p1Dialect : p2Dialect;
        const targetDialect = speaker === 'P1' ? p2Dialect : p1Dialect;

        try {
            const translatedText = await nativeTranslate(text, targetDialect, sourceDialect, targetLanguage, sourceLanguage);
            setChatLog(prev => [...prev, { sender: speaker, original: text, translated: translatedText, sourceLanguage, targetLanguage, sourceD: sourceDialect, targetD: targetDialect }]);
            speak(translatedText, targetLanguage, targetDialect);
        } catch (e) { console.error('Translation failed:', e); window.alert('Translation unavailable. Nothing was sent or spoken.'); } finally { setIsProcessing(false); }
    };

    const { status: p1Status, startRecording: startP1, stopRecording: stopP1 } = useReactMediaRecorder({ audio: true, onStop: (url, blob) => handleTurn(url, blob, "P1") });
    const { status: p2Status, startRecording: startP2, stopRecording: stopP2 } = useReactMediaRecorder({ audio: true, onStop: (url, blob) => handleTurn(url, blob, "P2") });

    return (
        <div className="game-layout">
            <div className="listener-header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '20px' }}>🏠←</button>
                <h3 style={{ margin: 0, color: '#22c55e', flex: 1 }}>CONVO BRIDGE</h3>
                <button onClick={() => setChatLog([])} style={{ background: 'rgba(0,0,0,0.3)', border: 'none', borderRadius: '8px', padding: '4px 8px', color: '#ef4444', fontSize: '12px' }}>🗑️ CLEAR</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', height: '100%' }}>

                {/* PLAYER 2 (Top) */}
                <div className="phc-input-area" style={{ background: 'rgba(0,0,0,0.4)', borderTop: '3px solid #f472b6', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <select className="cyber-input" value={p2Language} onChange={handleP2LangChange} style={{ color: '#f472b6', flex: 1 }}>
                            {availableLanguages.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                        <select className="cyber-input" value={p2Dialect} onChange={e => setP2Dialect(e.target.value)} style={{ color: '#f472b6', flex: 1 }}>
                            {p2AvailableDialects.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input value={p2Text} onChange={e => setP2Text(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleTextTurn(p2Text, "P2")} placeholder="P2: Type and press Enter (4,000 characters max)…" disabled={isProcessing || p1Status === "recording" || p2Status === "recording"} style={{ flex: 1, background: '#1e293b', border: '1px solid #f472b6', color: '#fff', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
                        <button onClick={() => handleTextTurn(p2Text, "P2")} disabled={isProcessing || !p2Text.trim()} style={{ background: '#f472b6', color: '#000', border: 'none', borderRadius: '8px', padding: '0 14px', fontWeight: 'bold', cursor: 'pointer', fontSize: '16px' }}>→</button>
                    </div>
                    <button className={`record-btn ${p2Status === "recording" ? "pulsing" : ""}`} onPointerDown={startP2} onPointerUp={stopP2} onPointerLeave={stopP2} disabled={isProcessing || p1Status === "recording"} style={{ width: '100%', padding: '12px', fontSize: '20px', background: p2Status === "recording" ? '#ef4444' : '#334155', userSelect: 'none', touchAction: 'none' }}>
                        {p2Status === "recording" ? "🔴" : isProcessing ? "⏳" : "🎙️"}
                    </button>
                    <div style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Hold to record</div>
                </div>

                {/* CHAT LOG (Middle) */}
                <div className="phc-chat-window" style={{ background: '#0f172a' }}>
                    {chatLog.length === 0 && <div style={{ textAlign: 'center', color: '#64748b', marginTop: '30px' }}>Hold a button or type to start translating.</div>}
                    {chatLog.map((msg, i) => (
                        <div key={i} style={{ alignSelf: msg.sender === "P1" ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                            <p style={{ fontSize: '10px', color: '#94a3b8', margin: '0 0 3px 0', textAlign: msg.sender === "P1" ? 'right' : 'left' }}>{msg.sourceD} → {msg.targetD}</p>
                            <div style={{ background: msg.sender === "P1" ? 'rgba(56,189,248,0.15)' : 'rgba(244,114,182,0.15)', padding: '8px 12px', borderRadius: '10px', border: `1px solid ${msg.sender === "P1" ? '#38bdf8' : '#f472b6'}` }}>
                                <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#e2e8f0' }}>{msg.original}</p>
                                <p style={{ margin: 0, fontSize: '12px', color: '#facc15', fontStyle: 'italic' }}>🌐 {msg.translated}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* PLAYER 1 (Bottom) */}
                <div className="phc-input-area" style={{ background: 'rgba(0,0,0,0.4)', borderBottom: '3px solid #38bdf8', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <select className="cyber-input" value={p1Language} onChange={handleP1LangChange} style={{ color: '#38bdf8', flex: 1 }}>
                            {availableLanguages.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                        <select className="cyber-input" value={p1Dialect} onChange={e => setP1Dialect(e.target.value)} style={{ color: '#38bdf8', flex: 1 }}>
                            {p1AvailableDialects.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input value={p1Text} onChange={e => setP1Text(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleTextTurn(p1Text, "P1")} placeholder="P1: Type and press Enter (4,000 characters max)…" disabled={isProcessing || p2Status === "recording" || p1Status === "recording"} style={{ flex: 1, background: '#1e293b', border: '1px solid #38bdf8', color: '#fff', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
                        <button onClick={() => handleTextTurn(p1Text, "P1")} disabled={isProcessing || !p1Text.trim()} style={{ background: '#38bdf8', color: '#000', border: 'none', borderRadius: '8px', padding: '0 14px', fontWeight: 'bold', cursor: 'pointer', fontSize: '16px' }}>→</button>
                    </div>
                    <button className={`record-btn ${p1Status === "recording" ? "pulsing" : ""}`} onPointerDown={startP1} onPointerUp={stopP1} onPointerLeave={stopP1} disabled={isProcessing || p2Status === "recording"} style={{ width: '100%', padding: '12px', fontSize: '20px', background: p1Status === "recording" ? '#ef4444' : '#334155', userSelect: 'none', touchAction: 'none' }}>
                        {p1Status === "recording" ? "🔴" : isProcessing ? "⏳" : "🎙️"}
                    </button>
                    <div style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Hold to record</div>
                </div>

            </div>
        </div>
    );
}

// ==========================================
// 📡 GAME 6: PureHumCom (Automated Matchmaking)
// ==========================================
function GameHumanConvo({ userKey, operator, dialects, languageHierarchy, onBack, setXP }) {
    const [inferenceError, setInferenceError] = useState("");
    const reportInferenceError = (message) => {
        setInferenceError(String(message));
        window.alert(message);
    };

    const inferenceMetadataRef = useRef({});
    const [phase, setPhase] = useState(() => localStorage.getItem(`phc_phase_${userKey}`) || "setup"); // setup -> lobby -> chat
    const [myLanguage, setMyLanguage] = useState(() => localStorage.getItem(`phc_myLang_${userKey}`) || "English");
    const [myDialect, setMyDialect] = useState(() => localStorage.getItem(`phc_myD_${userKey}`) || dialects[0] || "General English");
    const [customD, setCustomD] = useState("");
    const [customL, setCustomL] = useState("");
    const [aiLanguage, setAiLanguage] = useState("English");
    const handleAiLangChange = (e) => {
        const nextLanguage = e.target.value;
        setAiLanguage(nextLanguage);
        setAiDialect(languageHierarchy?.[nextLanguage]?.find(d => !d.startsWith("+")) || "");
    };
    const [lobbyUsers, setLobbyUsers] = useState([]);
    const [aiDialect, setAiDialect] = useState(dialects[0] || "General English");
    const [isHosting, setIsHosting] = useState(false);

    const [activeRooms, setActiveRooms] = useState(() => {
        return readExpiringConversationState(`phc_active_rooms_${userKey}`) || {};
    });
    const [currentRoom, setCurrentRoom] = useState(() => localStorage.getItem(`phc_current_room_${userKey}`) || "");

    useEffect(() => {
        localStorage.setItem(`phc_phase_${userKey}`, phase);
        localStorage.setItem(`phc_myLang_${userKey}`, myLanguage);
        localStorage.setItem(`phc_myD_${userKey}`, myDialect);
        writeExpiringConversationState(`phc_active_rooms_${userKey}`, activeRooms);
        localStorage.setItem(`phc_current_room_${userKey}`, currentRoom);
    }, [phase, myLanguage, myDialect, activeRooms, currentRoom, userKey]);

    const availableLanguages = Object.keys(languageHierarchy || { English: dialects });
    const availableDialects = languageHierarchy?.[myLanguage] || [myDialect];
    
    const handleMyLangChange = (e) => {
        const newLang = e.target.value;
        setMyLanguage(newLang);
        const newDialects = languageHierarchy?.[newLang] || (newLang === "English" ? dialects : []);
        setMyDialect(newDialects[0] || "");
    };

    // 🟢 NEW: Fetch XP specifically for the selected dialect in PureHumCom
    useEffect(() => {
        const fetchXP = async () => {
            const d = myDialect;
            if (!d || d === "+ Add New Dialect") return;

            try {
                const app = await getClient();
                const res = await app.predict("/get_user_xp", [operator, d]);
                const newXp = JSON.parse(res.data[0]).xp;
                mergeDialectXp(setXP, d, newXp);
            } catch(e) {
                console.error("XP Sync Error", e);
            }
        };
        fetchXP();
    }, [myDialect, operator, setXP]);

    const [transcribedText, setTranscribedText] = useState("");

    // 🟢 NEW: State for tweaking AI interpretation
    const [clarification, setClarification] = useState("");
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [showInterpretation, setShowInterpretation] = useState(false);

    const [isSending, setIsSending] = useState(false);
    const [contributeCurrentTurn, setContributeCurrentTurn] = useState(false);
    const [autoPlayAudio, setAutoPlayAudio] = useState(true);
    const [currentAudioBlob, setCurrentAudioBlob] = useState(null);
    const phcMachineTranscriptRef = useRef("");
    const phcMachineOutputRef = useRef("");
    const phcTaskIdRef = useRef("");
    const phcTurnIdRef = useRef("");
    const phcStartedAtRef = useRef("");

    const currentChatLog = useMemo(() => activeRooms[currentRoom]?.chatLog || [], [activeRooms, currentRoom]);
    const currentPartnerDialect = activeRooms[currentRoom]?.partnerDialect || "";
    const currentPartnerLanguage = activeRooms[currentRoom]?.partnerLanguage || "";
    const chatLogRef = useRef(currentChatLog);
    const endOfMessagesRef = useRef(null);
    const lastSpokenRef = useRef({}); // { [room]: lastIndex }

    const getMessageKeys = useCallback((msg) => {
        const signature = [msg?.sender, msg?.original, msg?.translation, msg?.dialect, msg?.target_dialect, msg?.inputType].join("||");
        return msg?.id ? [msg.id, signature] : [signature];
    }, []);

    const appendMessages = useCallback((room, msgs) => {
        const incoming = Array.isArray(msgs) ? msgs : [msgs];
        setActiveRooms(prev => {
            const currentLog = prev[room]?.chatLog || [];
            const seen = new Set(currentLog.reduce((keys, msg) => keys.concat(getMessageKeys(msg)), []));
            const uniqueMsgs = incoming.filter(msg => {
                const keys = getMessageKeys(msg);
                if (keys.some(key => seen.has(key))) return false;
                keys.forEach(key => seen.add(key));
                return true;
            });
            if (uniqueMsgs.length === 0) return prev;
            return {
                ...prev,
                [room]: {
                    ...prev[room],
                    chatLog: [...currentLog, ...uniqueMsgs]
                }
            };
        });
    }, [getMessageKeys]);

    useEffect(() => {
        chatLogRef.current = currentChatLog;

        // Handle TTS for new incoming messages
        if (autoPlayAudio && currentChatLog.length > 0) {
            const latestMsg = currentChatLog[currentChatLog.length - 1];
            const lastSpokenIdx = lastSpokenRef.current[currentRoom] || -1;
            
            if (latestMsg.sender !== operator && currentChatLog.length - 1 > lastSpokenIdx) {
                lastSpokenRef.current[currentRoom] = currentChatLog.length - 1;
                const textToSpeak = latestMsg.translation || latestMsg.original;
                const languageToSpeak = latestMsg.translation ? latestMsg.target_language : latestMsg.source_language;
                const speechDialect = latestMsg.translation ? latestMsg.target_dialect : latestMsg.source_dialect || latestMsg.dialect;
                speakSelectedLanguage(textToSpeak, languageToSpeak, speechDialect);
            }
        }
    }, [currentChatLog, operator, autoPlayAudio, currentRoom]);

    // 1. MATCHMAKING / LOBBY POLLING
    useEffect(() => {
        if (phase !== "lobby") return;
        const pollLobby = async () => {
            try {
                const app = await getClient();
                const resMatch = await app.predict("/check_match", [operator]);
                const dataMatch = JSON.parse(resMatch.data[0]);

                if (dataMatch.status === "matched") {
                    const room = dataMatch.room_id;
                    setActiveRooms(prev => ({
                        ...prev,
                        [room]: prev[room] || { partnerLanguage: dataMatch.partner_language, partnerDialect: dataMatch.partner_dialect, chatLog: [], unread: 0 }
                    }));
                    setCurrentRoom(room);
                    setPhase("chat");
                    setIsHosting(false);
                    return;
                }

                const resLobby = await app.predict("/get_lobby", []);
                const currentLobby = JSON.parse(resLobby.data[0]);
                setLobbyUsers(currentLobby.filter(u => u.id !== operator));
            } catch (e) { console.warn("Lobby sync failed..."); }
        };

        const interval = setInterval(pollLobby, 3000);
        pollLobby();
        return () => clearInterval(interval);
    }, [phase, operator]);

    const activeRoomsRef = useRef(activeRooms);
    useEffect(() => {
        activeRoomsRef.current = activeRooms;
    }, [activeRooms]);

    // 2. CHAT POLLING
    useEffect(() => {
        if (phase !== "chat" || !currentRoom) return;
        const pollMessages = async () => {
            try {
                const app = await getClient();
                const currentLength = chatLogRef.current.length;
                const res = await app.predict("/relay_poll", [currentRoom, currentLength]);
                const newMsgs = JSON.parse(res.data[0]);

                if (newMsgs && newMsgs.length > 0) {
                    appendMessages(currentRoom, newMsgs);
                    setTimeout(() => endOfMessagesRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
                }
            } catch (e) { console.error("Poll failed", e); }
        };

        const interval = setInterval(pollMessages, 3000);
        return () => clearInterval(interval);
    }, [phase, currentRoom, appendMessages]);

    // 3. BACKGROUND POLLING
    useEffect(() => {
        const pollBackground = async () => {
            try {
                const app = await getClient();
                const bgRooms = Object.keys(activeRoomsRef.current).filter(r => r !== currentRoom || phase !== "chat");
                for (const room of bgRooms) {
                    const roomData = activeRoomsRef.current[room];
                    const currentLength = roomData.chatLog.length;
                    const res = await app.predict("/relay_poll", [room, currentLength]);
                    const newMsgs = JSON.parse(res.data[0]);
                    
                    if (newMsgs && newMsgs.length > 0) {
                        appendMessages(room, newMsgs);
                        setActiveRooms(prev => ({
                            ...prev,
                            [room]: {
                                ...prev[room],
                                unread: (prev[room]?.unread || 0) + newMsgs.length
                            }
                        }));
                    }
                }
            } catch (e) { }
        };

        const interval = setInterval(pollBackground, 15000);
        return () => clearInterval(interval);
    }, [currentRoom, phase, appendMessages]);

    const handleEnterLobby = () => {
        const final_d = myDialect === "+ Add New Dialect" ? customD : myDialect;
        if (!final_d.trim()) { reportInferenceError("Please enter a dialect name."); return; }
        setPhase("lobby");
    };

    const handleJoinUser = async (targetId) => {
        setInferenceError("");
        const final_d = myDialect === "+ Add New Dialect" ? customD : myDialect;
        try {
            const app = await getClient();
            const result = await app.predict("/join_queue_v3", [operator, resolveLanguage(myLanguage, customL), final_d, targetId]);
            const joined = JSON.parse(result?.data?.[0] || "{}");
            if (!["waiting", "matched"].includes(joined.status)) throw new Error(joined.message || "Join failed.");
        } catch (e) { reportInferenceError("Failed to connect to peer."); }
    };

    const handleHostRoom = async () => {
        setInferenceError("");
        const final_d = myDialect === "+ Add New Dialect" ? customD : myDialect;
        try {
            const app = await getClient();
            const result = await app.predict("/join_queue_v3", [operator, resolveLanguage(myLanguage, customL), final_d, ""]);
            const joined = JSON.parse(result?.data?.[0] || "{}");
            if (!["waiting", "matched"].includes(joined.status)) throw new Error(joined.message || "Hosting failed.");
            setIsHosting(true);
            window.alert("You are now hosting! Waiting for a peer to connect...");
        } catch (e) { reportInferenceError("Failed to host."); }
    };

    const handleCancelSearch = async () => {
        setPhase("setup");
        setIsHosting(false);
        try {
            const app = await getClient();
            await app.predict("/leave_queue", [operator]);
        } catch (e) { }
    };
    
    const handleLeaveRoom = async (room) => {
        setActiveRooms(prev => {
            const next = { ...prev };
            delete next[room];
            return next;
        });
        if (currentRoom === room) {
            setPhase("lobby");
            setCurrentRoom("");
            setTranscribedText("");
            setClarification("");
            setShowInterpretation(false);
        }
    };

    const handleStartAiChat = async () => {
        setInferenceError("");
        const room = `ai_room_${operator}_${Date.now()}`;
        const final_d = myDialect === "+ Add New Dialect" ? customD : myDialect;
        try {
            const app = await getClient();
            const sourceLanguage = resolveLanguage(myLanguage, customL);
            const introText = await replyText(app, "Introduce yourself briefly as Echo and invite a conversation.", "", "English", "Standard English", aiLanguage, aiDialect);
            const userIntroTranslation = await translateText(app, introText, aiLanguage, aiDialect, sourceLanguage, final_d);
            setActiveRooms(prev => ({
                ...prev,
                [room]: {
                    partnerLanguage: aiLanguage,
                    partnerDialect: aiDialect,
                    chatLog: [{
                        sender: "ai_echo", original: introText, translation: userIntroTranslation,
                        source_language: aiLanguage, source_dialect: aiDialect, dialect: aiDialect,
                        target_language: sourceLanguage, target_dialect: final_d, inputType: "text",
                    }],
                    unread: 0,
                },
            }));
            setCurrentRoom(room);
            setPhase("chat");
        } catch (error) {
            reportInferenceError("Echo is unavailable. No reply was generated. " + error.message);
        }
    };

    // 🟢 NEW: Dedicated function to generate meaning from text
    const handleRegenerateMeaning = async () => {
        setInferenceError("");
        if (!transcribedText.trim()) return;
        setClarification("");
        setIsRegenerating(true);
        setShowInterpretation(true);
        try {
            const app = await getClient();
            const final_d = myDialect === "+ Add New Dialect" ? customD : myDialect;
            const cRes = await clarifyText(app, transcribedText, resolveLanguage(myLanguage, customL), final_d);
            inferenceMetadataRef.current = cRes.inference;
            let cText = cRes.data[0];
            try { cText = JSON.parse(cText).clarification || cText; } catch { }
            setClarification(cText);
            if (!phcMachineOutputRef.current) phcMachineOutputRef.current = cText;
            setIsRegenerating(false);
        } catch (e) {
            setClarification(""); reportInferenceError("Meaning generation failed. Please retry.");
            setIsRegenerating(false);
        }
    };

    const [isTrimming, setIsTrimming] = useState(false);
    const [isReopeningAudioEditor, setIsReopeningAudioEditor] = useState(false);

    const processAudio = async (blob) => {
        setIsTrimming(false);
        setIsReopeningAudioEditor(false);
        setTranscribedText("⏳ Transcribing audio...");
        setCurrentAudioBlob(blob);
        phcTaskIdRef.current = createCollectionId("task");
        phcTurnIdRef.current = createCollectionId("turn");
        phcStartedAtRef.current = new Date().toISOString();
        try {
            const app = await getClient();
            const final_d = myDialect === "+ Add New Dialect" ? customD : myDialect;

            // 1. Get Transcription
            const res = await app.predict("/transcribe_check", [wrapAudioBlobForUpload(await alterVoiceForCloudUpload(blob)), asrHint(resolveLanguage(myLanguage, customL), final_d)]);
            const text = res.data[0];
            setTranscribedText(text);
            phcMachineTranscriptRef.current = text;

            // 🟢 NEW: 2. Auto-fetch AI interpretation for review
            setIsRegenerating(true);
            setShowInterpretation(true);
            const cRes = await clarifyText(app, text, resolveLanguage(myLanguage, customL), final_d);
            inferenceMetadataRef.current = cRes.inference;
            let cText = cRes.data[0];
            try { cText = JSON.parse(cText).clarification || cText; } catch { }
            setClarification(cText);
            phcMachineOutputRef.current = cText;
            setIsRegenerating(false);

        } catch (e) {
            setClarification(""); reportInferenceError("Transcription or meaning generation failed. Please retry.");
            setIsRegenerating(false);
        }
    };

    const handleAudioStop = async (blobUrl, blob) => {
        setCurrentAudioBlob(blob);
        setIsReopeningAudioEditor(false);
        setIsTrimming(true); // Open the trimmer UI instead of immediately processing
    };

    const { status, startRecording, stopRecording } = useReactMediaRecorder({ audio: true, onStop: handleAudioStop });

    const handleSend = async (inputType = 'voice') => {
        setInferenceError("");
        try { resolveLanguage(myLanguage, customL); } catch (error) { reportInferenceError(error.message); return; }
        if (!validMeaning(clarification) || isRegenerating) { reportInferenceError("Generate or enter a valid reviewed meaning first."); return; }
        if (!currentPartnerLanguage) { reportInferenceError("This older room has no verified partner language. Reconnect to continue."); return; }
        if (!transcribedText.trim()) return;
        const shouldContribute = contributeCurrentTurn;
        setContributeCurrentTurn(false);
        setIsSending(true);
        const final_d = myDialect === "+ Add New Dialect" ? customD : myDialect;
        const textToSend = transcribedText.trim();
        const meaningToSend = clarification.trim(); // 🟢 NEW: Capture the tweaked meaning
        const sourceLanguage = resolveLanguage(myLanguage, customL);
        const submittedAt = new Date().toISOString();
        const startedAt = phcStartedAtRef.current || submittedAt;
        const taskId = phcTaskIdRef.current || createCollectionId("task");
        const turnId = phcTurnIdRef.current || createCollectionId("turn");
        

        try {
            const app = await getClient();

            if (currentRoom.startsWith("ai_")) {
                const userTargetTranslation = await translateText(app, meaningToSend, "English", "Standard English", currentPartnerLanguage, currentPartnerDialect);
                const generatedReply = await replyText(app, textToSend, meaningToSend, sourceLanguage, final_d, currentPartnerLanguage, currentPartnerDialect);
                const translatedReply = await translateText(app, generatedReply, currentPartnerLanguage, currentPartnerDialect, sourceLanguage, final_d);
                appendMessages(currentRoom, [
                    { sender: operator, original: textToSend, translation: userTargetTranslation,
                      source_language: sourceLanguage, source_dialect: final_d, dialect: final_d,
                      target_language: currentPartnerLanguage, target_dialect: currentPartnerDialect, standard_meaning: meaningToSend, inputType },
                    { sender: "ai_echo", original: generatedReply, translation: translatedReply,
                      source_language: currentPartnerLanguage, source_dialect: currentPartnerDialect, dialect: currentPartnerDialect,
                      target_language: sourceLanguage, target_dialect: final_d, inputType: "text" },
                ]);
            } else {
                const relayRes = await app.predict("/relay_send_v3", [currentRoom, operator, textToSend, sourceLanguage, final_d, currentPartnerLanguage, currentPartnerDialect, meaningToSend]);
                const relayPayload = JSON.parse(relayRes?.data?.[0] || "{}");
                if (relayPayload.status !== "success" || !relayPayload.message || !validMeaning(relayPayload.message.translation)) {
                    throw new Error("Relay did not confirm a translated message.");
                }
                appendMessages(currentRoom, [relayPayload.message]);
            }
            // Research contribution is a separate, per-turn, opt-in action.
            if (shouldContribute) try {
                await submitCollectionSubmission({
                    participant_id: getCollectionParticipantId(userKey, "pureversation"),
                    app_id: APP_IDENTITY.name,
                    app_version: APP_VERSION,
                    game_id: "purehumcom",
                    task_id: taskId,
                    prompt_text: "",
                    topic_id: "",
                    image_asset_id: "",
                    conversation_id: createCollectionId("conversation"),
                    turn_id: turnId,
                    preceding_context: "",
                    source_language: sourceLanguage,
                    source_dialect: final_d,
                    target_language: currentRoom?.startsWith("ai_") ? currentPartnerLanguage : "not_collected",
                    target_dialect: currentRoom?.startsWith("ai_") ? (currentPartnerDialect || "synthetic") : "not_collected",
                    input_modality: inputType,
                    source_text: textToSend,
                    machine_transcript_initial: phcMachineTranscriptRef.current,
                    user_transcript_final: textToSend,
                    machine_output_initial: phcMachineOutputRef.current,
                    user_output_final: meaningToSend,
                    asr_model: inputType === "voice" ? "Whisper tiny" : "",
                    model_name: inferenceMetadataRef.current.model || "",
                    model_version: "",
                    translation_route: inferenceMetadataRef.current.route || "",
                    started_at: startedAt,
                    submitted_at: submittedAt,
                    duration_ms: Math.max(0, Date.parse(submittedAt) - Date.parse(startedAt)),
                    consent_version: COLLECTION_CONSENT_VERSION,
                    metadata: {
                        source_tag: "Game: PureHumCom",
                        room_type: currentRoom?.startsWith("ai_") ? "human_ai" : "human_human",
                        output_semantics: "reviewed_standard_meaning",
                    },
                });
                
                // No optimistic reward is granted for an unconfirmed contribution.
            } catch (contributionError) {
                console.warn("Research submission failed", contributionError?.name || "Error");
                notifyContributionNotRecorded(contributionError);
            }
            phcMachineTranscriptRef.current = "";
            phcMachineOutputRef.current = "";
            phcTaskIdRef.current = createCollectionId("task");
            phcTurnIdRef.current = createCollectionId("turn");
            phcStartedAtRef.current = new Date().toISOString();

            setTranscribedText("");
            setClarification("");
            setShowInterpretation(false);
            setCurrentAudioBlob(null);
        } catch (e) {
            reportInferenceError("Failed to send message.");
        } finally {
            setIsSending(false);
        }
    };

    if (phase === "setup") {
        return (
            <div className="game-layout">
                {inferenceError && <div role="alert" style={{ color: "#fca5a5", border: "1px solid #ef4444", padding: "10px", marginBottom: "12px" }}>{inferenceError}</div>}
                <div className="mission-card phc-setup-card">
                    <div className="icon-large">📡</div>
                    <h2 style={{ color: '#f97316' }}>GLOBAL NETWORK</h2>
                    <p style={{ fontSize: '12px', color: '#cbd5e1', marginBottom: '20px' }}>Join the pool to securely match with another Operator.</p>

                    <label style={{ fontSize: '10px', color: '#94a3b8' }}>YOUR LANGUAGE:</label>
                    <select className="cyber-input" value={myLanguage} onChange={handleMyLangChange} style={{ marginBottom: '10px', color: '#f97316' }}>
                        {availableLanguages.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                    {myLanguage === "+ Add New Language" && <input className="cyber-input" aria-label="Your custom language" value={customL} onChange={e => setCustomL(e.target.value)} />}


                    <label style={{ fontSize: '10px', color: '#94a3b8' }}>YOUR DIALECT:</label>
                    <select className="cyber-input" value={myDialect} onChange={e => setMyDialect(e.target.value)} style={{ marginBottom: myDialect === "+ Add New Dialect" ? '10px' : '30px', color: '#f97316' }}>
                        {availableDialects.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>

                    {myDialect === "+ Add New Dialect" && (
                        <div style={{ background: 'rgba(0,0,0,0.5)', padding: '10px', borderRadius: '8px', border: '1px solid #f97316', marginBottom: '30px' }}>
                            <label style={{ fontSize: '10px', color: '#f97316', marginBottom: '5px', display: 'block' }}>NEW DIALECT NAME:</label>
                            <input type="text" className="cyber-input" value={customD} onChange={e => setCustomD(e.target.value)} placeholder="e.g., Nigerian Pidgin" style={{ marginBottom: 0 }} />
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="cancel-btn" onClick={onBack} style={{ flex: 1 }}>BACK</button>
                        <button className="cyber-button" onClick={handleEnterLobby} style={{ flex: 2, background: '#f97316', color: '#000' }}>ENTER NETWORK LOBBY</button>
                    </div>
                </div>
            </div>
        );
    }

    if (phase === "lobby") {
        return (
            <div className="game-layout">
                {inferenceError && <div role="alert" style={{ color: "#fca5a5", border: "1px solid #ef4444", padding: "10px", marginBottom: "12px" }}>{inferenceError}</div>}
                <div className="mission-card phc-setup-card">
                    <h3 style={{ color: '#f97316', textAlign: 'center', marginBottom: '5px' }}>NETWORK LOBBY</h3>
                    <p style={{ fontSize: '11px', color: '#cbd5e1', textAlign: 'center', marginBottom: '20px' }}>Select an active peer to connect, or host your own connection.</p>

                    <div style={{ flex: 1, overflowY: 'auto', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '10px', border: '1px solid #334155', marginBottom: '15px' }}>
                        {Object.keys(activeRooms).length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ fontSize: '12px', color: '#38bdf8', marginBottom: '10px', fontWeight: 'bold' }}>YOUR ACTIVE CONNECTIONS</div>
                                {Object.entries(activeRooms).map(([rCode, rData]) => (
                                    <div key={rCode} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1e293b', padding: '10px 15px', borderRadius: '8px', marginBottom: '8px', border: '1px solid #38bdf8' }}>
                                        <div>
                                            <div style={{ color: '#fff', fontSize: '13px', fontWeight: 'bold' }}>{rData.partnerDialect.toUpperCase()}</div>
                                            <div style={{ color: '#64748b', fontSize: '10px' }}>{rCode.substring(0, 10)}...</div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            {rData.unread > 0 && <span style={{ background: '#ef4444', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '10px', fontWeight: 'bold' }}>{rData.unread} NEW</span>}
                                            <button onClick={() => { setCurrentRoom(rCode); setPhase("chat"); }} className="cyber-button" style={{ padding: '6px 12px', fontSize: '11px', background: '#38bdf8', color: '#000' }}>RESUME</button>
                                            <button onClick={() => handleLeaveRoom(rCode)} className="cyber-button" style={{ padding: '6px 12px', fontSize: '11px', background: 'transparent', color: '#ef4444', border: '1px solid #ef4444' }}>CLOSE</button>
                                        </div>
                                    </div>
                                ))}
                                <div style={{ height: '1px', background: '#334155', margin: '15px 0' }}></div>
                            </div>
                        )}
                        <div style={{ fontSize: '12px', color: '#f97316', marginBottom: '10px', fontWeight: 'bold' }}>AVAILABLE PEERS</div>
                        {lobbyUsers.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#64748b', marginTop: '30px' }}>
                                <div className="radar-spinner" style={{ width: '40px', height: '40px', borderRadius: '50%', border: '2px solid #f97316', borderTopColor: 'transparent', animation: 'spin 1s linear infinite', margin: '0 auto 15px auto' }}></div>
                                {isHosting ? "Broadcasting... Waiting for a peer to join" : "No active peers. Host a room!"}
                                <div style={{ marginTop: '20px', maxWidth: '200px', margin: '20px auto 0 auto' }}>
                                    <label style={{ fontSize: '10px', color: '#a78bfa', display: 'block', marginBottom: '5px' }}>CHOOSE AI DIALECT:</label>
                                    <label>Echo language</label>
                                    <select className="cyber-input" value={aiLanguage} onChange={handleAiLangChange}>
                                        {availableLanguages.filter(language => !language.startsWith("+") && language !== "Other").map(language => <option key={language} value={language}>{language}</option>)}
                                    </select>
                                    <select
                                        className="cyber-input"
                                        value={aiDialect}
                                        onChange={e => setAiDialect(e.target.value)}
                                        style={{ marginBottom: '15px', borderColor: '#a78bfa', color: '#a78bfa', padding: '8px' }}
                                    >
                                        {(languageHierarchy?.[aiLanguage] || []).filter(d => d !== "+ Add New Dialect").map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                    <button
                                        onClick={handleStartAiChat}
                                        className="cyber-button"
                                        style={{ background: '#a78bfa', color: '#000', width: '100%' }}
                                    >
                                        🤖 Chat with AI Echo
                                    </button>
                                </div>
                            </div>
                        ) : (
                            lobbyUsers.map(u => (
                                <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1e293b', padding: '15px', borderRadius: '8px', marginBottom: '10px', border: '1px solid #f97316' }}>
                                    <div>
                                        <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>{u.dialect}</div>
                                        <div style={{ color: '#64748b', fontSize: '10px' }}>ID: {u.id.substring(0, 6)}...</div>
                                    </div>
                                    <button onClick={() => handleJoinUser(u.id)} className="cyber-button" style={{ padding: '8px 15px', fontSize: '12px', background: '#f97316', color: '#000' }}>CONNECT</button>
                                </div>
                            ))
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="cancel-btn" onClick={handleCancelSearch} style={{ flex: 1 }}>LEAVE LOBBY</button>
                        <button className="cyber-button" onClick={handleHostRoom} style={{ flex: 2, background: '#38bdf8', color: '#000' }}>HOST CONNECTION</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="game-layout" style={{ display: 'flex', flexDirection: 'column' }}>
                {inferenceError && <div role="alert" style={{ color: "#fca5a5", border: "1px solid #ef4444", padding: "10px", marginBottom: "12px" }}>{inferenceError}</div>}
            <div className="listener-header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexShrink: 0 }}>
                <button onClick={() => handleLeaveRoom(currentRoom)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '20px' }} title="Leave Room">🔌</button>
                <button onClick={() => setPhase("lobby")} style={{ background: 'transparent', border: 'none', color: '#facc15', cursor: 'pointer', fontSize: '20px' }} title="Back to Lobby">🏠</button>
                <h3 style={{ margin: 0, color: '#f97316', flex: 1, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>LINKED: {currentPartnerDialect.toUpperCase()}</h3>
                
                <button
                    onClick={() => {
                        const roomKeys = Object.keys(activeRooms);
                        if (roomKeys.length > 1) {
                            const currentIndex = roomKeys.indexOf(currentRoom);
                            const nextIndex = (currentIndex + 1) % roomKeys.length;
                            const nextRoom = roomKeys[nextIndex];
                            setActiveRooms(prev => ({ ...prev, [nextRoom]: { ...prev[nextRoom], unread: 0 } }));
                            setCurrentRoom(nextRoom);
                        }
                    }}
                    style={{ background: '#334155', border: '1px solid #94a3b8', color: '#fff', borderRadius: '8px', padding: '5px 10px', cursor: 'pointer', fontSize: '12px', opacity: Object.keys(activeRooms).length > 1 ? 1 : 0.5 }}
                    disabled={Object.keys(activeRooms).length <= 1}
                    title="Swap Active Rooms"
                >
                    ⇄ SWAP ({Object.keys(activeRooms).length})
                </button>

                <button
                    onClick={() => {
                        setAutoPlayAudio(!autoPlayAudio);
                        window.speechSynthesis.cancel();
                    }}
                    style={{ background: 'transparent', border: '1px solid #f97316', color: '#f97316', borderRadius: '8px', padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}
                >
                    {autoPlayAudio ? '🔊' : '🔈'}
                </button>
            </div>

            {/* CHAT WINDOW */}
            <div className="phc-chat-window">
                {currentChatLog.length === 0 && <div style={{ textAlign: 'center', color: '#64748b', marginTop: '50px' }}>Connection secured. Type or speak to start.</div>}

                {currentChatLog.map((msg, i) => {
                    const isMe = msg.sender === operator;
                    return (
                        <div key={i} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                            <div style={{ fontSize: '9px', color: '#64748b', textAlign: isMe ? 'right' : 'left', marginBottom: '2px' }}>
                                {msg.dialect} {msg.inputType === 'text' ? '⌨️' : '🎙️'}
                            </div>
                            <div style={{ background: isMe ? 'rgba(249, 115, 22, 0.2)' : 'rgba(56, 189, 248, 0.2)', padding: '10px', borderRadius: '12px', border: `1px solid ${isMe ? '#f97316' : '#38bdf8'}` }}>
                                <p style={{ margin: 0, color: '#fff', fontSize: '14px' }}>{msg.original}</p>
                            </div>
                            {/* Translation bubble */}
                            <div style={{ background: '#1e293b', borderLeft: `3px solid ${isMe ? '#facc15' : '#a78bfa'}`, padding: '8px 10px', marginTop: '4px', borderRadius: '6px', fontSize: '12px' }}>
                                <strong style={{ color: isMe ? '#facc15' : '#a78bfa' }}>🌐 {msg.target_dialect || 'Translation'}:</strong>{' '}
                                <span style={{ color: '#cbd5e1' }}>{msg.translation || msg.original}</span>
                            </div>
                        </div>
                    );
                })}
                <div ref={endOfMessagesRef} />
            </div>

            {/* INPUT AREA */}
            <div className="phc-input-area">

                {/* 🟢 NEW: Interpretation / Meaning Tweak UI */}
                {isTrimming && (
                    <AudioTrimmer 
                        blob={currentAudioBlob} 
                        onTrim={processAudio} 
                        onCancel={() => {
                            setIsTrimming(false);
                            if (!isReopeningAudioEditor) {
                                setCurrentAudioBlob(null);
                                setTranscribedText("");
                                setClarification("");
                                setShowInterpretation(false);
                            }
                            setIsReopeningAudioEditor(false);
                        }}
                        cancelLabel={isReopeningAudioEditor ? "CLOSE" : "DISCARD"}
                    />
                )}
                {currentAudioBlob && !isTrimming && (
                    <button
                        type="button"
                        onClick={() => {
                            setIsReopeningAudioEditor(true);
                            setIsTrimming(true);
                        }}
                        disabled={isSending || status === "recording"}
                        title="Reopen the local recording to listen, trim, and rerun transcription"
                        style={{ width: '100%', marginBottom: '8px', background: '#1e293b', color: '#f97316', border: '1px solid #f97316', borderRadius: '8px', padding: '7px 8px', fontSize: '11px', fontWeight: 'bold', cursor: isSending ? 'not-allowed' : 'pointer' }}
                    >
                        🎧 REVIEW / EDIT AUDIO
                    </button>
                )}
                {!isTrimming && showInterpretation && (
                    <div style={{ marginBottom: '10px', background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '8px', border: '1px solid #38bdf8' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                            <label className="hitl-label">AI MEANING (EDIT IF WRONG):</label>
                            <button className="hitl-action-btn" onClick={handleRegenerateMeaning} disabled={isRegenerating}>
                                {isRegenerating ? "UPDATING..." : "🔄 REGENERATE"}
                            </button>
                        </div>
                        <textarea
                            className="hitl-edit-box"
                            value={clarification}
                            onChange={e => setClarification(e.target.value)}
                            placeholder="AI interpretation will appear here..."
                            style={{ border: '1px solid #38bdf8', color: '#34d399', fontStyle: 'italic', marginBottom: 0, padding: '8px', fontSize: '13px' }}
                        />
                    </div>
                )}

                {showInterpretation && (
                    <label style={{ display: 'flex', gap: '8px', marginBottom: '8px', fontSize: '11px', color: '#cbd5e1' }}>
                        <input type="checkbox" checked={contributeCurrentTurn} onChange={event => setContributeCurrentTurn(event.target.checked)} />
                        Also contribute this reviewed turn to the moderated research queue (off by default).
                    </label>
                )}

                {/* Persistent text input row */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                        value={transcribedText}
                        onChange={e => setTranscribedText(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !isSending) {
                                if (showInterpretation) handleSend(currentAudioBlob ? 'voice' : 'text');
                                else handleRegenerateMeaning();
                            }
                        }}
                        placeholder="Type a message…"
                        disabled={status === 'recording' || isSending}
                        style={{ flex: 1, background: '#1e293b', border: '1px solid #f97316', color: '#fff', padding: '10px 14px', borderRadius: '10px', fontSize: '14px', outline: 'none' }}
                    />
                    {!showInterpretation ? (
                        <button
                            onClick={handleRegenerateMeaning}
                            title="Translate to reveal Send button"
                            disabled={!transcribedText.trim() || isRegenerating}
                            style={{ background: '#334155', color: '#38bdf8', border: 'none', borderRadius: '10px', padding: '0 12px', fontSize: '16px', cursor: 'pointer' }}
                        >
                            🧠
                        </button>
                    ) : (
                        <button
                            onClick={() => handleSend(currentAudioBlob ? 'voice' : 'text')}
                            disabled={isSending || !transcribedText.trim() || !validMeaning(clarification) || isRegenerating || status === 'recording'}
                            style={{ background: '#f97316', color: '#000', border: 'none', borderRadius: '10px', padding: '0 16px', fontWeight: 'bold', cursor: 'pointer', fontSize: '18px', flexShrink: 0 }}
                        >→</button>
                    )}
                </div>
                {/* Voice record button */}
                <div style={{ width: '100%' }}>
                    <button
                        className={`record-btn ${status === "recording" ? "pulsing" : ""}`}
                        onPointerDown={startRecording}
                        onPointerUp={stopRecording}
                        onPointerLeave={stopRecording}
                        disabled={isSending}
                        style={{ width: '100%', padding: '14px', fontSize: '20px', background: status === "recording" ? '#ef4444' : '#334155', userSelect: 'none', touchAction: 'none' }}
                    >
                        {status === "recording" ? "🔴" : isSending ? "⏳" : "🎙️"}
                    </button>
                    <div style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', marginTop: '5px' }}>Hold to record</div>
                </div>
            </div>
        </div>
    );
}


function AudioTrimmer({ blob, onTrim, onCancel, cancelLabel = "DISCARD" }) {
    const [start, setStart] = useState(0);
    const [end, setEnd] = useState(100);
    const [duration, setDuration] = useState(0);
    const [blobUrl, setBlobUrl] = useState("");
    const audioRef = useRef(null);

    useEffect(() => {
        if (!blob) return undefined;
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [blob]);

    const handleLoadedMetadata = () => {
        if (audioRef.current && audioRef.current.duration !== Infinity) {
            setDuration(audioRef.current.duration);
        }
    };

    const trimAudio = async () => {
        if (!blob || duration === 0 || (start === 0 && end === 100)) return onTrim(blob);
        const startTime = (start / 100) * duration;
        const endTime = (end / 100) * duration;

        try {
            const arrayBuffer = await blob.arrayBuffer();
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            const startSample = Math.floor(startTime * audioBuffer.sampleRate);
            const endSample = Math.floor(endTime * audioBuffer.sampleRate);
            const trimmedLength = endSample - startSample;

            if (trimmedLength <= 0) return onTrim(blob);

            const offlineContext = new window.OfflineAudioContext(
                audioBuffer.numberOfChannels,
                trimmedLength,
                audioBuffer.sampleRate
            );
            const source = offlineContext.createBufferSource();
            const newBuffer = offlineContext.createBuffer(
                audioBuffer.numberOfChannels,
                trimmedLength,
                audioBuffer.sampleRate
            );

            for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
                const channelData = audioBuffer.getChannelData(channel);
                const newChannelData = newBuffer.getChannelData(channel);
                for (let i = 0; i < trimmedLength; i++) {
                    newChannelData[i] = channelData[startSample + i];
                }
            }

            source.buffer = newBuffer;
            source.connect(offlineContext.destination);
            source.start(0);
            const renderedBuffer = await offlineContext.startRendering();
            onTrim(bufferToWav(renderedBuffer, renderedBuffer.length));
        } catch (e) {
            console.error("Trimming failed, returning original", e);
            onTrim(blob);
        }
    };

    return (
        <div style={{ background: '#1e293b', padding: '15px', borderRadius: '8px', border: '1px solid #f97316', marginBottom: '10px' }}>
            <div style={{ color: '#f97316', fontSize: '12px', marginBottom: '10px', fontWeight: 'bold' }}>AUDIO REVIEW &amp; TRIMMING (Optional)</div>
            <audio ref={audioRef} controls src={blobUrl} onLoadedMetadata={handleLoadedMetadata} style={{ width: '100%', height: '35px', marginBottom: '15px', outline: 'none' }} />
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '10px', color: '#94a3b8', width: '35px' }}>START</span>
                <input type="range" min="0" max="100" value={start} onChange={e => { if (Number(e.target.value) < end) setStart(Number(e.target.value)); }} style={{ flex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
                <span style={{ fontSize: '10px', color: '#94a3b8', width: '35px' }}>END</span>
                <input type="range" min="0" max="100" value={end} onChange={e => { if (Number(e.target.value) > start) setEnd(Number(e.target.value)); }} style={{ flex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={onCancel} className="cancel-btn" style={{ flex: 1, padding: '10px' }}>{cancelLabel}</button>
                <button onClick={trimAudio} className="cyber-button" style={{ flex: 1, background: '#f97316', color: '#000', padding: '10px' }}>APPLY & ANALYZE</button>
            </div>
        </div>
    );
}

function bufferToWav(abuffer, len) {
    const numOfChan = abuffer.numberOfChannels;
    const length = len * numOfChan * 2 + 44;
    const buffer = new ArrayBuffer(length);
    const view = new DataView(buffer);
    const channels = [];
    let offset = 0;
    let pos = 0;

    function setUint16(data) { view.setUint16(pos, data, true); pos += 2; }
    function setUint32(data) { view.setUint32(pos, data, true); pos += 4; }

    setUint32(0x46464952); setUint32(length - 8); setUint32(0x45564157);
    setUint32(0x20746d66); setUint32(16); setUint16(1); setUint16(numOfChan);
    setUint32(abuffer.sampleRate); setUint32(abuffer.sampleRate * 2 * numOfChan);
    setUint16(numOfChan * 2); setUint16(16);
    setUint32(0x61746164); setUint32(length - pos - 4);

    for (let i = 0; i < abuffer.numberOfChannels; i++) channels.push(abuffer.getChannelData(i));

    while (pos < length) {
        for (let i = 0; i < numOfChan; i++) {
            const sample = Math.max(-1, Math.min(1, channels[i][offset]));
            view.setInt16(pos, (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0, true);
            pos += 2;
        }
        offset++;
    }

    return new Blob([buffer], { type: "audio/wav" });
}


// ==========================================
// ⚙️ SHARED GAME LAYOUT 
// ==========================================
// 🟢 Added onChangeTopic to the props
function SharedGameLayout({ title, mission, recStatus, startRec, stopRec, mediaBlob, clearBlobUrl, dialects, languageHierarchy, setDialects, userKey, operator, setXP, onBack, onNext, timer, mode, sourceTag, onReset, onChangeTopic }) {
    const inferenceMetadataRef = useRef({});
    const [step, setStep] = useState("RECORD");
    const [contributeReviewedEntry, setContributeReviewedEntry] = useState(false);
    const [researchSubmissionError, setResearchSubmissionError] = useState("");
    const submittingEntryRef = useRef(false);
    const reviewPanelRef = useRef(null);
    const [textInput, setTextInput] = useState("");
    const [transcribed, setTranscribed] = useState("");
    const [clarification, setClarification] = useState("");
    const [tone, setTone] = useState("Neutral / Conversational");
    const [context, setContext] = useState("General");
    const [pragmatics, setPragmatics] = useState("");
    const [isRegenerating, setIsRegenerating] = useState(false);
    const machineTranscriptInitialRef = useRef("");
    const machineOutputInitialRef = useRef("");
    const taskIdRef = useRef("");
    const startedAtRef = useRef("");
    const [language, setLanguage] = useState(() => localStorage.getItem(`${sourceTag || title}_language_${userKey}`) || "English");
    const [dialect, setDialect] = useState(() => localStorage.getItem(`${sourceTag || title}_dialect_${userKey}`) || dialects[0] || "General");

    useEffect(() => {
        localStorage.setItem(`${sourceTag || title}_language_${userKey}`, language);
        localStorage.setItem(`${sourceTag || title}_dialect_${userKey}`, dialect);
    }, [sourceTag, title, userKey, language, dialect]);

    const [customLanguage, setCustomLanguage] = useState("");
    const [customD, setCustomD] = useState("");
    const contributionAllowed = hasVerifiedCollectionConsent(getCollectionParticipantId(userKey, "pureversation"), APP_IDENTITY.name, mode === "vision" ? "multimodal" : (mediaBlob ? "speech" : "text"));
    useEffect(() => {
        if (step === "RECORD" || !contributionAllowed) setContributeReviewedEntry(false);
        if (step === "RECORD") setResearchSubmissionError("");
    }, [step, contributionAllowed]);
    const [captcha, setCaptcha] = useState("");
    const [captchaMath, setCaptchaMath] = useState({ a: 3, b: 4 });

    const availableLanguages = [...new Set([...Object.keys(languageHierarchy || { English: [] }), language])];
    const availableDialects = [...new Set([...(languageHierarchy?.[language] || []), dialect].filter(Boolean))];

    useEffect(() => {
        taskIdRef.current = createCollectionId("task");
        startedAtRef.current = new Date().toISOString();
        machineTranscriptInitialRef.current = "";
        machineOutputInitialRef.current = "";
    }, [mission?.text, mission?.subtext, mission?.image]);

    useEffect(() => {
        if (availableDialects.length > 0 && !availableDialects.includes(dialect)) { setDialect(availableDialects[0]); }
    }, [availableDialects, dialect]);

    const handleLanguageChange = (e) => {
        const newLang = e.target.value;
        setLanguage(newLang);
        const newDialects = languageHierarchy?.[newLang] || (newLang === "English" ? dialects : []);
        setDialect(newDialects[0] || "");
    };

    useEffect(() => {
        if (dialect === "+ Add New Dialect") {
            setCaptchaMath({
                a: Math.floor(Math.random() * 10) + 1,
                b: Math.floor(Math.random() * 10) + 1
            });
            setCaptcha("");
        }
    }, [dialect]);

    // 🟢 NEW: Fetch XP specifically for the selected dialect, with local dictionary fallback
    useEffect(() => {
        const fetchXP = async () => {
            const d = dialect === "+ Add New Dialect" ? customD : dialect;
            if (!d || d === "+ Add New Dialect") return;

            try {
                const app = await getClient();
                const res = await app.predict("/get_user_xp", [operator, d]);
                const newXp = JSON.parse(res.data[0]).xp;
                mergeDialectXp(setXP, d, newXp);
            } catch(e) {
                console.error("XP Sync Error", e);
            }
        };
        fetchXP();
    }, [dialect, customD, operator, setXP]);

    const [lastProcessedBlob, setLastProcessedBlob] = useState(null);
    useEffect(() => {
        if (mediaBlob && mediaBlob !== lastProcessedBlob && step === "RECORD") {
            setLastProcessedBlob(mediaBlob);
            handleAnalyze();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaBlob, step, lastProcessedBlob]);

    useEffect(() => {
        if (step === "REVIEW") {
            window.setTimeout(() => {
                reviewPanelRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
            }, 60);
        }
    }, [step]);

    const handleAnalyze = async () => {
        if (!mediaBlob) return;
        setClarification("");
        setStep("ANALYZING");
        try {
            const blob = await fetch(mediaBlob).then(r => r.blob());
            const app = await getClient();
            const d = dialect === "+ Add New Dialect" ? customD : dialect;
            const tRes = await app.predict("/transcribe_check", [wrapAudioBlobForUpload(await alterVoiceForCloudUpload(blob)), asrHint(resolveLanguage(language, customLanguage), d)]);
            const text = tRes.data[0];
            setTranscribed(text);
            machineTranscriptInitialRef.current = text;
            const cRes = await clarifyText(app, text, resolveLanguage(language, customLanguage), d);
            inferenceMetadataRef.current = cRes.inference;
            machineOutputInitialRef.current = parseClarification(cRes.data[0]);
            setStep("REVIEW");
        } catch (e) {
            setClarification(""); setStep("RECORD"); window.alert("Audio or meaning generation failed. Please retry.");
        }
    };

    const handleTextSubmit = async () => {
        if (!textInput.trim()) return;
        setClarification("");
        setStep("ANALYZING");
        setTranscribed(textInput);
        machineTranscriptInitialRef.current = "";
        try {
            const app = await getClient();
            const d = dialect === "+ Add New Dialect" ? customD : dialect;
            const cRes = await clarifyText(app, textInput, resolveLanguage(language, customLanguage), d);
            inferenceMetadataRef.current = cRes.inference;
            machineOutputInitialRef.current = parseClarification(cRes.data[0]);
            setStep("REVIEW");
        } catch (e) {
            setClarification(""); setStep("RECORD"); window.alert("Meaning generation failed. Please retry.");
        }
    };

    const parseClarification = (rawData) => {
        let data;
        try { data = JSON.parse(rawData); } catch { data = { clarification: rawData }; }
        setClarification(data.clarification || data.Meaning || rawData);
        setContext(data.context || "General");
        setPragmatics(data.pragmatics || "");
        return data.clarification || data.Meaning || rawData;
    };

    const handleRegenerate = async () => {
        if (!transcribed.trim()) return;
        setClarification("");
        setIsRegenerating(true);
        try {
            const app = await getClient();
            const d = dialect === "+ Add New Dialect" ? customD : dialect;
            const res = await clarifyText(app, transcribed, resolveLanguage(language, customLanguage), d);
            inferenceMetadataRef.current = res.inference;
            parseClarification(res.data[0]);
            setIsRegenerating(false);
        } catch (e) { setClarification(""); window.alert("Meaning generation failed. Please retry."); setIsRegenerating(false); }
    };

    const handleSubmit = async () => {
        if (submittingEntryRef.current) return;
        if (!contributeReviewedEntry || !contributionAllowed) {
            setResearchSubmissionError("Choose whether to contribute this reviewed entry. You can continue without contributing.");
            return;
        }
        setResearchSubmissionError("");
        if (!validMeaning(clarification) || isRegenerating) { window.alert("A valid reviewed meaning is required."); return; }
        if (dialect === "+ Add New Dialect") {
            if (!customD.trim()) { alert("Please type a new dialect name."); return; }
            if (parseInt(captcha) !== (captchaMath.a + captchaMath.b)) {
                alert(`Bot Check Failed! ${captchaMath.a} + ${captchaMath.b} = ${captchaMath.a + captchaMath.b}.`);
                return;
            }
        }

        submittingEntryRef.current = true;
        setStep("SUBMITTING");
        try {
            const d = dialect === "+ Add New Dialect" ? customD : dialect;
            const sourceLanguage = language === "+ Add New Language" ? customLanguage : language;
            const submittedAt = new Date().toISOString();
            const startedAt = startedAtRef.current || submittedAt;
            const taskId = taskIdRef.current || createCollectionId("task");
            const contributionReceipt = await submitCollectionSubmission({
                participant_id: getCollectionParticipantId(userKey, "pureversation"),
                app_id: APP_IDENTITY.name,
                app_version: APP_VERSION,
                game_id: toCollectionGameId(sourceTag || title),
                task_id: taskId,
                prompt_text: String(mission?.text || ""),
                topic_id: String(mission?.subtext || ""),
                image_asset_id: String(mission?.image || ""),
                conversation_id: "",
                turn_id: "",
                preceding_context: "",
                source_language: sourceLanguage || "",
                source_dialect: d,
                target_language: "English",
                target_dialect: "Standard English",
                input_modality: mode === "vision" ? "multimodal" : (mediaBlob ? "voice" : "text"),
                source_text: transcribed,
                machine_transcript_initial: machineTranscriptInitialRef.current,
                user_transcript_final: transcribed,
                machine_output_initial: machineOutputInitialRef.current,
                user_output_final: clarification,
                asr_model: mediaBlob ? "Whisper tiny" : "",
                model_name: inferenceMetadataRef.current.model || "",
                model_version: "",
                translation_route: inferenceMetadataRef.current.route || "",
                started_at: startedAt,
                submitted_at: submittedAt,
                duration_ms: Math.max(0, Date.parse(submittedAt) - Date.parse(startedAt)),
                consent_version: COLLECTION_CONSENT_VERSION,
                metadata: { tone, context, pragmatics, source_tag: sourceTag || "Unknown Game", mode: mode || "" },
            });
            if (!/^sub_[0-9a-f]{32}$/.test(String(contributionReceipt?.submission_id || contributionReceipt?.data?.submission_id || ""))) {
                throw new Error("The collection service did not return a submission receipt.");
            }
            // Removed optimistic XP increment to enforce smart-contract verification
            if (customD && dialect === "+ Add New Dialect") {
                setDialects(prev => {
                    const clean = prev.filter(d => d !== "+ Add New Dialect");
                    return Array.from(new Set([customD, ...clean, "+ Add New Dialect"]));
                });
                setDialect(customD);
            }
            setStep("💎 Contribution Saved!");
            setTranscribed(""); setClarification(""); setCaptcha("");
            machineTranscriptInitialRef.current = "";
            machineOutputInitialRef.current = "";

            setTimeout(() => {
                if (onNext) {
                    if (clearBlobUrl) clearBlobUrl();
                    setLastProcessedBlob(null);
                    setTextInput("");
                    setStep("RECORD");
                    // 🟢 FIX: Pass the user's transcript & clarification UP to the game engine
                    onNext(transcribed, clarification);
                } else { onBack(); }
            }, 1500);

        } catch (e) {
            setResearchSubmissionError("Research contribution could not be confirmed."
                + (e?.collectionStatus ? " HTTP " + e.collectionStatus + "." : "")
                + " Your reviewed text remains here. Retry, or continue without contributing.");
            setStep("REVIEW");
        } finally {
            submittingEntryRef.current = false;
        }
    };

    const handleContinueWithoutContribution = () => {
        if (submittingEntryRef.current || isRegenerating) return;
        const reviewedTranscript = transcribed;
        const reviewedMeaning = clarification;
        setContributeReviewedEntry(false);
        setResearchSubmissionError("");
        if (clearBlobUrl) clearBlobUrl();
        setLastProcessedBlob(null);
        setTextInput("");
        setTranscribed("");
        setClarification("");
        setStep("RECORD");
        if (onNext) onNext(reviewedTranscript, reviewedMeaning);
        else onBack?.();
    };

    return (
        <div className={`game-layout shared-game-layout ${step === "REVIEW" ? "review-mode" : ""}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {onBack && <button className="back-icon" onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '20px', padding: '0' }} title="Back to Menu">🏠←</button>}
                    <div>
                        <h3 style={{ margin: 0, color: '#f8fafc' }}>{title}</h3>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    {/* 🟢 NEW: The manual Change Topic button */}
                    {onChangeTopic && (<button onClick={onChangeTopic} style={{ background: 'rgba(244, 114, 182, 0.2)', border: '1px solid #f472b6', borderRadius: '8px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px', color: '#f472b6', fontWeight: 'bold' }} title="Change Topic">🔄 TOPIC</button>)}
                    {onReset && (<button onClick={onReset} style={{ background: 'rgba(244, 114, 182, 0.2)', border: '1px solid #f472b6', borderRadius: '8px', padding: '4px 8px', cursor: 'pointer', fontSize: '16px', color: '#f472b6', fontWeight: 'bold' }} title="Reset this game">🔄🎮</button>)}
                </div>
            </div>

            {mode === "vision" ? (
                <div className="vision-mode-container" style={{ position: 'relative', width: '100%', height: '250px', borderRadius: '12px', overflow: 'hidden', marginBottom: '15px', border: '1px solid #a78bfa' }}>
                    <div className="vision-image" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${mission.image})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 1 }} />

                    {/* 🟢 FIX: Locked text to the bottom of the image with absolute positioning and a dark gradient for guaranteed visibility on Android */}
                    <div className="vision-text-block" style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', padding: '20px 10px 10px 10px', background: 'linear-gradient(to top, rgba(0,0,0,0.95), transparent)', textAlign: 'center', textShadow: '1px 1px 2px #000', zIndex: 2, boxSizing: 'border-box' }}>
                        {mission.subtext && <p className="subtext" style={{ color: '#facc15', margin: '0 0 5px 0', fontSize: '11px', letterSpacing: '1px' }}>{mission.subtext}</p>}
                        <p className="main-text" style={{ color: '#ffffff', fontWeight: 'bold', margin: 0, fontSize: '15px' }}>{mission.text}</p>
                    </div>
                </div>
            ) : (
                <div className="mission-card">
                    <div className="doodle-bg" style={{ backgroundImage: `url(${mission.image})` }} />
                    {/* 🟢 FIX: Added heavy text-shadow and forced bright colors for Speed Chat & Archivist */}
                    <div className="mission-content-overlay" style={{ textShadow: '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 0px 4px 10px rgba(0,0,0,0.9)' }}>
                        <div className="mission-copy">
                            {mission.subtext && <p className="mission-subtext">{mission.subtext}</p>}
                            <p className="mission-main-text">{mission.text}</p>
                        </div>
                    </div>
                </div>
            )}

            <div className={`control-panel ${step === "REVIEW" ? "is-reviewing" : ""}`}>
                {(step === "RECORD" || step === "REVIEW") && (
                    <div className="dialect-selector" style={{ marginBottom: '10px' }}>
                        <label>PLEASE CHOOSE OR ADD YOUR TARGET LANGUAGE / DIALECT.</label>
                        <select value={language} onChange={handleLanguageChange} style={{ marginBottom: '10px' }}>
                            {availableLanguages.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                        <select value={dialect} onChange={e => setDialect(e.target.value)}>
                            {availableDialects.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                        
                        {language === "+ Add New Language" && (
                            <div style={{ marginTop: '10px', background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px', border: '1px dotted #38bdf8' }}>
                                <input className="cyber-input" placeholder="Enter Language Name (e.g. Gaelic)" value={customLanguage} onChange={e => setCustomLanguage(e.target.value)} style={{ marginBottom: '10px', width: '90%' }} />
                            </div>
                        )}

                        {dialect === "+ Add New Dialect" && (
                            <div style={{ marginTop: '10px', background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px', border: '1px dotted #38bdf8' }}>
                                <input className="cyber-input" placeholder="Enter Dialect Name (e.g. Scottish Gaelic)" value={customD} onChange={e => setCustomD(e.target.value)} style={{ marginBottom: '10px', width: '90%' }} />
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>Human Check: {captchaMath.a} + {captchaMath.b} =</span>
                                    <input className="cyber-input" style={{ width: '60px', padding: '5px', textAlign: 'center' }} type="number" value={captcha} onChange={e => setCaptcha(e.target.value)} />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {step === "RECORD" && (
                    <div style={{ textAlign: 'center', marginTop: '10px' }}>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
                            <input
                                value={textInput}
                                onChange={e => setTextInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleTextSubmit()}
                                placeholder="Type a message or use the mic..."
                                disabled={recStatus === 'recording'}
                                style={{ flex: 1, background: '#1e293b', border: '1px solid #f97316', color: '#fff', padding: '10px 14px', borderRadius: '10px', fontSize: '14px', outline: 'none' }}
                            />
                            <button
                                onClick={handleTextSubmit}
                                disabled={!textInput.trim() || recStatus === 'recording'}
                                style={{ background: '#f97316', color: '#000', border: 'none', borderRadius: '10px', padding: '0 16px', fontWeight: 'bold', cursor: 'pointer', fontSize: '18px', flexShrink: 0 }}
                            >→</button>
                        </div>

                        {timer !== undefined && recStatus === "recording" && (
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: timer <= 3 ? '#ef4444' : '#38bdf8', marginBottom: '15px', textShadow: '0 0 10px rgba(56, 189, 248, 0.5)' }}>
                                {/* 🟢 FIX: Displays TIME'S UP when the clock hits zero */}
                                {timer === 0 ? "🛑 TIME'S UP!" : `⏱️ 00:${timer < 10 ? `0${timer}` : timer}`}
                            </div>
                        )}
                        <button
                            className={`cyber-button ${recStatus === 'recording' ? 'recording pulsing' : ''}`}
                            onPointerDown={startRec} onPointerUp={stopRec} onTouchStart={startRec} onTouchEnd={stopRec} onPointerLeave={stopRec}
                            style={{ width: '100%', padding: '20px', fontSize: '20px', backgroundColor: recStatus === 'recording' ? '#ef4444' : 'rgba(0,0,0,0.5)', userSelect: 'none', touchAction: 'none' }}
                        >
                            {recStatus === "recording" ? "🔴" : "🎙️"}
                        </button>
                        <div style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', marginTop: '5px' }}>Hold to record</div>
                    </div>
                )}

                {(step === "ANALYZING" || step === "SUBMITTING") && <div className="loading">PROCESSING...</div>}

                {step === "REVIEW" && (
                    <div className="review-box" ref={reviewPanelRef}>
                        {mediaBlob && (
                            <div className="audio-preview review-audio-preview"><label style={{ fontSize: '10px', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>REVIEW RECORDING:</label><audio src={mediaBlob} controls style={{ width: '100%', borderRadius: '8px' }} /></div>
                        )}
                        <div className="input-group"><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><label className="hitl-label">I {mediaBlob ? "HEARD" : "READ"} (EDIT IF NEEDED):</label></div><textarea className="hitl-edit-box" value={transcribed} onChange={e => setTranscribed(e.target.value)} /></div>
                        <div className="input-group"><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}><label className="hitl-label" style={{ margin: 0 }}>MEANING:</label><button className="hitl-action-btn" onClick={handleRegenerate} disabled={isRegenerating}>{isRegenerating ? "UPDATING..." : "🔄 REGENERATE FROM TEXT"}</button></div><textarea className="hitl-edit-box" value={clarification} onChange={e => setClarification(e.target.value)} /></div>
                        <div className="input-group"><label>TONE:</label><select value={tone} onChange={e => setTone(e.target.value)}>{TONES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                        <ResearchContributionChoice checked={contributeReviewedEntry} onChange={setContributeReviewedEntry} available={contributionAllowed} />
                        {researchSubmissionError && <p className="research-submission-error" role="alert">{researchSubmissionError}</p>}
                        <div className="action-row research-review-actions">
                            <button className="cancel-btn" onClick={() => { if (clearBlobUrl) clearBlobUrl(); setLastProcessedBlob(null); setTextInput(""); setStep("RECORD"); }}>RETRY</button>
                            <button type="button" className="cancel-btn" onClick={handleContinueWithoutContribution} disabled={isRegenerating}>CONTINUE WITHOUT CONTRIBUTING</button>
                            <button className="cyber-button" onClick={handleSubmit} disabled={!contributeReviewedEntry || !contributionAllowed || !validMeaning(clarification) || isRegenerating}>SUBMIT FOR REVIEW</button>
                        </div>
                    </div>
                )}

                {/* 🟢 FIX: Call the CustomEvent to open the Feedback Modal */}
                <div style={{ textAlign: 'center', marginTop: '15px', paddingBottom: '10px' }}>
                    <button onClick={() => window.dispatchEvent(new CustomEvent('open-feedback'))} style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer' }}>
                        💬 Feedback (Unavailable)
                    </button>
                </div>
            </div>
        </div>
    );
}


// ==========================================
// 🟢 GAME: BURN WARD (Graveyard & Appeals)
// ==========================================
function GameBurnWard({ onBack }) {
    return (
        <div className="game-container" style={{ borderColor: GAME_ASSETS.BURNWARD.color, boxShadow: `0 0 20px ${GAME_ASSETS.BURNWARD.color}40` }}>
            <div className="game-header">
                <button className="icon-btn" onClick={onBack} title="Back to Hub">Hub</button>
                <div style={{ flex: 1, textAlign: 'center' }}>
                    <h2 style={{ margin: 0, color: GAME_ASSETS.BURNWARD.color }}>BURN WARD</h2>
                </div>
            </div>
            <div className="game-content" style={{ textAlign: 'center', padding: '30px' }}>
                <h3 style={{ color: GAME_ASSETS.BURNWARD.color }}>PUBLIC APPEAL QUEUE UNAVAILABLE</h3>
                <p>Rejected contribution content and audio are not exposed to public game clients.</p>
                <p>Appeals and dataset decisions require authenticated, scoped admin review.</p>
            </div>
        </div>
    );
}

export { GameArchivist, GameSpeedChat, GameVisionQuest, GameActiveListener, GameConvoBridge, GameHumanConvo, SharedGameLayout, GameBurnWard };
export default App;
